/**
 * ConnectionManager — unified WebSocket connection lifecycle manager.
 *
 * Owns both Socket.IO (engine) and API Gateway WebSocket connections.
 * Handles reconnection, token refresh, subscription tracking, and
 * connection status in one place.
 */

import Cookies from 'js-cookie';
import io, { Socket as IOSocket } from 'socket.io-client';
import { getAuthToken, isGuestSession, subscribeAuthChange } from 'utils/auth';
import { reportConnectionLost } from 'utils/paceGuardrails';
import { recordActionMessage } from 'utils/perfCounters';
import { hasUnhydratedTable } from 'utils/tableHydrationState';
import { markAllTablesResyncing, markTableResyncing } from 'utils/tableResyncState';
import { isValidTournamentId, warnBadTournamentId } from 'utils/tournamentSubscriptions';
import { APP_DOMAIN, DESCOPE_COOKIE, SOCKET_SERVER_URL, UPDATE_SOCKET_URL } from 'utils/urls';

import { RawWebSocketAdapter, SocketIOLikeInterface } from './RawWebSocketAdapter';
import { TableFeedWatchdog, TableFeedWatchdogOptions } from './tableFeedWatchdog';

/** A disconnect the app asked for (logout, teardown): never a dropped connection. */
const DELIBERATE_DISCONNECT = 'deliberate';

/**
 * Monotonic milliseconds for the feed watchdog's accounting.
 *
 * Never `Date.now()`: wall clock jumps backwards on an NTP correction and
 * forwards on a device sleep/wake, which this app does on every Capacitor
 * foreground, and either one corrupts a pending repair deadline. Falls back to
 * wall clock only where `performance` is absent (older WebViews, jsdom).
 */
const monoNow = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/**
 * Health of the path the player's ACTIONS travel on, published separately from
 * `ConnectionStatus`.
 *
 * It has to be separate. `recomputeStatus` deliberately derives
 * `ConnectionStatus` from the UPDATE socket in dual mode, because a lagging or
 * absent legacy action socket must not pin a "reconnecting" banner while the game
 * is live on the serverless path (that was the stuck-banner bug, and the reasoning
 * is preserved in recomputeStatus's docblock). The cost of that choice was a
 * blind spot: `get_playermove` rides the action socket, so the app could report
 * 'connected' with the player unable to act at all. Rather than change what
 * `ConnectionStatus` means, this reports the action path on its own.
 *
 *   'ok'   — the action socket is connected
 *   'down' — no action socket, or it is disconnected
 *
 * Deliberately only two states. A third ('degraded': socket up but not
 * answering) needs a positive liveness signal, and the engine has none that is
 * both cheap and reliable — see the note on ACTION path liveness in
 * docs/investigations/20260902-en-10628-player-actions-silently-dropped.md.
 * `get_heartbeat` looks like the answer and is not: it has never carried
 * production traffic, it writes to the primary on every call, and it replies
 * whether or not the engine still holds this socket's table registration, so a
 * successful beat would not have meant what we needed it to mean. Detecting a
 * dropped action by its absence of effect (utils/actionOutcome) is the signal we
 * actually have, and it is evidence rather than inference.
 */
export type ActionPathStatus = 'ok' | 'down';

/**
 * Build the Socket.IO handshake auth. A guest session (not logged in) connects
 * with `{ guest: true }` (the engine accepts this for lobby-only viewing) —
 * regardless of any stale/expired token left in the cookie, which the engine
 * would otherwise reject. Logged-in users send the normal token pair.
 */
function buildSocketAuth(token: ReturnType<typeof getAuthToken>) {
  if (isGuestSession()) {
    return { guest: true };
  }
  return { access_token: token.access_token, refresh_token: token.refresh_token };
}

type Subscription = {
  gameID: string;
  gameType: string;
  playerGUID: string;
  username: string;
};

/** The wire shape of a SubscribeToGameUpdates payload, built from a tracked Subscription. */
function buildSubscribePayload(sub: Subscription) {
  return {
    gameID: sub.gameID,
    game: sub.gameType,
    playerGUID: sub.playerGUID,
    username: sub.username
  };
}

// Events that should route to the API Gateway WebSocket
const SERVERLESS_EMIT_EVENTS = [
  'SubscribeToGameUpdates',
  'UnsubscribeFromGameUpdates',
  'get_playerMoney',
  'get_playerPromos',
  'DismissScrollingNotification',
  'subscribeTournament',
  'unsubscribeTournament',
  'get_handHistory',
  // Own-cards backstop (EN-9775-V4): authenticated, per-hand lookup handled by
  // game-websocket-inbound's getOwnHandCards route, same as get_handHistory.
  'get_ownHandCards',
  // EN-10064: client pulls its initial colluder restriction set over game-ws on
  // (re)connect; the reply comes back as gotColluderRestrictions (also game-ws).
  'getColluderRestrictions',
  // EN-663 D1/EN-10228: the multi-run vote MUST reach the game-websocket-inbound
  // `voteMultiRun` Lambda, which is the only writer that derives the seat from the
  // authenticated connection, dedups per seat, and compare-and-swaps. Without this
  // entry the emit fell through to the legacy Socket.IO engine in `dual` mode — the
  // mode every shipped env selects — so none of that hardening took effect.
  'vote_MultiRun',
  // EN-11023: "straddle next hand" pre-action. Serverless only (the legacy engine
  // has no handler for it), so it is NOT in DUAL_EMIT_EVENTS.
  'straddle_PreAction'
];

// Events that arrive from API Gateway WebSocket
const GAME_UPDATE_EVENTS = [
  'gotTexas',
  'gotOmaha',
  'got_playerMoney',
  'got_playerPromos',
  'gotScrollingNotifications',
  'gotTnmStatus',
  'gotTnmRanking',
  'gotTnmPrizes',
  'gotTnmActiveTables',
  'gotSupportMessage',
  // EN-10064: colluder lobby-restriction markers. Pushed by the serverless
  // `colluder-broadcast` Lambda over API Gateway WebSocket (game-ws); the
  // legacy engine no longer emits it (off unless ENABLE_LEGACY_COLLUDER_BROADCAST
  // =true). Route it to the updateSocket — without this the listener falls
  // through to the legacy Socket.IO action socket and the push is never received.
  'gotColluderRestrictions',
  // Tournament balancing / hand-for-hand pause lifecycle. Pushed by the
  // tournamentBalancingBroadcast Lambda over API Gateway WebSocket, so they
  // must route to the updateSocket — without this they fall through to the
  // legacy Socket.IO action socket (which never emits them) and never fire.
  'gotTablePaused',
  'gotTableResumed',
  'gotTableBreaking',
  'gotHandForHandActive',
  'gotHandForHandEnded',
  'gotWaitingForTables',
  'gotHandHistory',
  // Response to get_ownHandCards, above — same route, same socket.
  'gotOwnHandCards',
  // Hint pushed over API Gateway WebSocket by the hand-history outbox drain
  // (shared/handHistorySnapshot/ready.js) once a completed hand is readable.
  'handHistoryReady',
  // EN-11151: the player's streak snapshot, pushed over game-ws by the streak
  // worker, the midnight job and the purchase API after every change. The
  // first read is REST (player-api GET /streaks/status), so there is no emit.
  'gotStreakStatus',
  // EN-11151: a seated player's frame changed; pushed to everyone at their
  // tables. The first read is REST (GET /streaks/frames).
  'gotStreakFrame'
];

// Events that can arrive from either socket.
//
// Table state (`gotTexas` / `gotOmaha`) and `got_playerMoney` are not here:
// they arrive only over API Gateway (cash-game-broadcast / balanceBroadcaster)
// and route via GAME_UPDATE_EVENTS. The engine's per-table push loop is
// commented out (`multiProcess` in apps/engine/texas/socket.js and
// omaha/socket.js). `get_playerMoney` is a SERVERLESS_EMIT_EVENT, so the
// game-websocket-inbound getPlayerMoney Lambda answers it over API Gateway; the
// engine's handler replies only on the Socket.IO socket that asked, and React
// never asks there. `got_playerGames` is the reverse:
// only the engine broadcaster sends it, so it takes the action-socket default.
const DUAL_LISTEN_EVENTS = [
  // Engine (Socket.IO) delivers the base payload; the player-broadcast Lambda
  // delivers the same data enriched with shouldAutoRedirect via WebSocket.
  // Listening on both lets the Lambda's data (which arrives later via SQS)
  // overwrite the engine's base payload in Redux.
  'gotPlayerTournamentDetails'
];

// Events that need to be emitted on BOTH sockets simultaneously.
//
// `SubscribeToGameUpdates` / `UnsubscribeFromGameUpdates` are dual-emit
// because the legacy engine and the API Gateway lambda each maintain their
// OWN subscriber list and neither knows about the other:
//
//   - Legacy Socket.IO engine: tracks subscribers in-memory; receiving
//     `SubscribeToGameUpdates` triggers `updateTableDebugMsg`, which rewrites
//     the table row's `debugMSG` to wake an idle table. WITHOUT this emit
//     reaching the engine, observing an idle table produces no broadcasts.
//     The engine does not push table state on Socket.IO; the resulting
//     updates reach the client over API Gateway (see DUAL_LISTEN_EVENTS).
//   - API Gateway lambda: writes the GAME#{type}{id}/CONN# row to DynamoDB
//     so the cash-game broadcaster can fan out future TABLE_UPDATE events.
//
// Unity is single-socket Socket.IO, so both responsibilities collapse into
// one delivery. React (dual-socket) used to send only to the API Gateway
// (because the event string was in SERVERLESS_EMIT_EVENTS), bypassing the
// engine wake-up. Each socket processes its emit exactly once — no
// duplicate-lambda race, since the two destinations are different routes.
//
// `vote_MultiRun` is here as well as in SERVERLESS_EMIT_EVENTS, deliberately.
// Sending it ONLY to API Gateway made the client depend on deploy order: there
// is no $default route, and RawWebSocketAdapter.emit acks locally on a timer
// rather than on a reply, so a client that shipped before the Lambda discarded
// every vote while the UI showed it as accepted and the tally never moved.
// Nothing in CI sequences the two — deploy_lowers.yml starts the client on
// `needs: build` while serverless-v2 waits on deploy_backoffice, so the client
// wins by default.
//
// Double delivery is a no-op: both writers resolve the seat from the
// authenticated connection, dedup per seat, and compare-and-swap on `runs`, and
// since EN-663 they share one eligibility predicate and one deadline. Sending to
// both is only safe BECAUSE of that — do not add an event here whose two
// handlers disagree.
const DUAL_EMIT_EVENTS = ['SubscribeToGameUpdates', 'UnsubscribeFromGameUpdates', 'vote_MultiRun'];

// The engine pushes per-player feeds (gotPlayerTournamentDetails, playerGames,
// live-chat notifications) only to socket ids registered by this event. A
// reconnect gives the action socket a new id and the engine drops the old one,
// so the manager re-announces it on every re-open (see reannounceOnline).
const ENGINE_ONLINE_EVENT = 'online';

export class ConnectionManager {
  private actionSocket: IOSocket | null = null;
  private updateSocket: RawWebSocketAdapter | null = null;
  private singleSocket: IOSocket | RawWebSocketAdapter | null = null;
  private mode: 'dual' | 'single-io' | 'single-ws' | null = null;
  /**
   * Defer the guest-to-member re-handshake until a location verdict exists.
   *
   * `$connect` enforces on the STORED verdict, and at the moment of login there
   * isn't one yet -- the client is still running the check. So the upgrade below
   * handshakes into a guaranteed refusal, retries on backoff, and the player
   * watches a connection error for as long as a GPS fix and a vendor round trip
   * take. Measured at ~9s on a real device; the cold-fix ceiling is far higher.
   *
   * Under `enforce` this is the NORMAL path, not an edge case: every first login
   * produces refused handshakes and retry churn before the verdict is written.
   *
   * GUESTS ARE DELIBERATELY UNAFFECTED. The hold gates only the upgrade to
   * member; a guest socket connects and browses exactly as it always has, and
   * `$connect` skips the geofence for it entirely.
   */
  private geoHold = false;
  /** An upgrade arrived while held, and must be performed on release. */
  private geoUpgradePending = false;
  private _status: ConnectionStatus = 'disconnected';
  // Why the connection is about to leave `connected` (EN-11182), and how many
  // times it has this session.
  private nextDropReason: string | null = null;
  private connectionDrops = 0;
  private statusListeners = new Set<(status: ConnectionStatus) => void>();
  private subscriptions = new Map<string, Subscription>();
  // Tournament (gotTnmStatus) feeds the app has subscribed to, by tournamentId.
  // Tracked alongside table subscriptions so BOTH are replayed on reconnect — a
  // WebSocket drop (e.g. a mobile network blip mid-hand) otherwise loses the
  // tournament feed silently: resubscribeAll only restored table game-updates, so
  // gotTnmStatus stopped and the client froze on its last-seen state (e.g. a stale
  // isPostBalancing:1 re-showing the "Final Table Rebalancing" overlay). The
  // centralized App-level subscription can't recover it either — it only
  // (re)subscribes when the SET of tournaments changes, which a reconnect doesn't.
  private tournamentSubscriptions = new Set<string>();
  private appListenerRemove: (() => void) | null = null;
  private authListenerRemove: (() => void) | null = null;
  // Access token already retried after the engine refused a handshake as
  // AUTH_EXPIRED. See handleAuthError: it bounds that retry to once per token.
  private expiredHandshakeRetryToken: string | null = null;
  // Whether the sockets currently in hand were handshaken as a GUEST.
  //
  // Handshake identity is fixed at socket-construction time — Socket.IO
  // serializes `auth` into the CONNECT packet, and RawWebSocketAdapter bakes
  // `?guest=true` into the WebSocket URL before `connected` is ever true — so it
  // cannot be repaired by assigning `auth` afterwards. Tracking it lets the auth
  // listener tell a guest→member UPGRADE (needs a full re-handshake) from an
  // ordinary token ROTATION (the in-place cycle is sufficient there).
  private handshakeWasGuest = false;
  // The playerGUID last announced via ENGINE_ONLINE_EVENT, per Socket.IO socket.
  // Keyed by instance because handshake identity is fixed per socket: a fresh
  // socket (re-handshake, account change) starts with nothing to replay and
  // waits for the app's own post-login announce.
  private onlinePlayerBySocket = new WeakMap<IOSocket, string>();
  private destroyed = false;
  private resubscribeTimer: ReturnType<typeof setTimeout> | null = null;
  // Timestamp (ms) of the last foreground-triggered re-subscribe, so returning
  // to the app repeatedly (rapid tab toggles) can't fan a re-subscribe storm.
  private lastForegroundResync = 0;
  // Timestamp (ms) when the app last went AWAY (visibility=hidden, window blur,
  // or Capacitor appStateChange isActive=false). null while the app is in the
  // foreground.
  //
  // The foreground re-subscribe path assumes we're returning from a real idle
  // (RCA 20260713 item C — silent tables need re-hydration when the app
  // resumes). But `focus`/`visibilitychange` also fire on trivial interactions
  // that never blank the felt: opening/closing DevTools, autocomplete popup
  // focus flicker, a modal opening in another surface, a native picker
  // dismissing. Without this timestamp the 5s throttle still lets one
  // resubscribeAll() through per interaction — a real SubscribeToGameUpdates
  // hits the wire and burns a Lambda invocation for nothing. Guest sessions
  // notice most because their table churn is minimal, so the re-subscribe
  // shows up as recurring log lines with no accompanying gameplay activity.
  private lastAwayAt: number | null = null;
  // Minimum away duration before a return-to-foreground triggers a re-hydrate
  // re-subscribe. Tuned to skip trivial focus toggles (browser DevTools,
  // dropdown flicker, modal focus swaps — all sub-second) while still catching
  // real device sleeps / tab switches / phone locks. If a real drop happened,
  // both sockets will be disconnected and the reconnect path handles it
  // regardless of this gate — this only guards the "sockets look fine, but
  // let's re-subscribe just in case" branch.
  private static MIN_AWAY_FOR_FOREGROUND_RESUBSCRIBE_MS = 30_000;
  // Minimum away duration before a return re-asserts subscriptions on the
  // ACTION socket. Much lower than the gate above because it is guarding a
  // different thing: that gate amortises a Lambda invocation, while the engine
  // assert costs four queries and no Lambda at all. So this floor only has to
  // exclude focus-chrome noise, not pay for itself.
  // Felt-staleness gate. A foreground re-subscribe re-arms the fan-out row but the
  // server pushes a table snapshot ONLY for a NEW subscription (subscribeGame
  // gates on isNewSubscription), so a same-connection re-subscribe of an idle
  // table pushes nothing and the felt stays blank (RCA 20260713 item C). After the
  // re-subscribe we wait briefly for a gotTexas/gotOmaha; if none lands, we force a
  // full reconnect (fresh connectionId ⇒ NEW subscription ⇒ server pushes the
  // snapshot). Cleared the instant a felt update arrives, so a healthy re-subscribe
  // never escalates to the heavier reconnect.
  private feltResyncTimer: ReturnType<typeof setTimeout> | null = null;

  // ── Action-path health (EN-10628) ──
  //
  // Everything below exists because the legacy action socket — the one every
  // fold/check/call/bet/raise travels on — had no status of its own, so it could
  // be dead, or attached to an engine worker that had forgotten this socket,
  // while the felt kept rendering from broadcasts on the OTHER socket and the
  // app reported 'connected'. See the ActionPathStatus docblock for why this is
  // connected/disconnected only and not a liveness probe.
  private _actionPathStatus: ActionPathStatus = 'down';
  private actionPathListeners = new Set<(status: ActionPathStatus) => void>();

  // ── Per-table feed watchdog (EN-10628) ──
  //
  // Every table frame the React client renders arrives over the UPDATE socket,
  // from the cash-game-broadcast Lambda, addressed by a DynamoDB row keyed on
  // (table, connection id). One row can go missing while the connection and the
  // other tables' rows are fine - a same-player subscribe from another device or
  // a lingering old connection evicts it, a 404/409 in the subscribe Lambda never
  // writes it, a lost update-socket leg never asks for it - and the Lambda tells
  // the client nothing in any of those cases (no route response is configured
  // and it sends no confirmation). So the client has to notice on its own.
  //
  // On the live broadcast path a table emits a frame at least every few seconds
  // whatever is happening on it (the engine re-enqueues every open table every
  // tick and nothing change-gates downstream), so silence past a threshold is a
  // sound signal - and a table the engine has stuck still streams, so a stall
  // does not trip it. The watchdog's bounds (a few repairs per silence episode,
  // first repairs offered ahead of repeats, wide jitter, a per-table floor and a
  // client-wide breaker that defer rather than cancel, and no reconnect path at
  // all) are what make it safe against
  // the reconnect storms this platform has seen before. See tableFeedWatchdog.ts.
  private feedWatchdog: TableFeedWatchdog;
  /**
   * Source of the escalation's jitter. Injectable so tests can pin the timings.
   *
   * A seam rather than a `Math.random` spy: jest's own source-map machinery
   * calls Math.random (source-map's quicksort), so mocking it globally blows the
   * stack before a single test runs.
   */
  private readonly random: () => number;
  /** Table frames whose gameID could not be resolved. See noteTableFrame. */
  private unattributedFrames = 0;
  private lastUnattributedWarnAt = Number.NEGATIVE_INFINITY;
  private static UNATTRIBUTED_WARN_EVERY_MS = 30_000;
  private feedWatchdogTimer: ReturnType<typeof setInterval> | null = null;
  // 2s, not 5s. The tick quantises every decision the watchdog makes, so a 5s
  // tick added up to 5s on top of the threshold and then up to 5s again after
  // the jitter expired - which on the acting table's 6s clock would have given
  // back most of what the tighter threshold buys. The work per tick is a Map
  // scan over at most four tables, negligible against the ~1 frame/s this
  // socket already parses.
  /**
   * A table whose last frame is newer than this is treated as having a working
   * feed, so `resyncTable` and the heartbeat both decline to re-assert it.
   *
   * 6s, from the staging frame-cadence measurement in the RCA rather than from
   * the busy-table figures alone. A QUIET table measured p50 1179ms, **p95
   * 3165ms, max 3486ms** - the publisher-side lock's 3s interval, which fires
   * whether or not a hand is running. An earlier 3s here sat BELOW that p95, so
   * a perfectly healthy quiet table would have failed the gate more than 5% of
   * the time and paid the full subscribe for nothing. 6s is ~1.7x the worst
   * healthy gap ever measured on this path, which is the same margin the RCA
   * uses to justify the acting table's 6s silence threshold.
   *
   * Note the polarity of the two errors. Too LOW wastes a subscribe on a healthy
   * table; too HIGH delays a repair by at most this value, against repair
   * thresholds of 6-20s. Neither can hide a broken table, because a broken table
   * is silent by definition.
   */
  private static FEED_LIVE_WITHIN_MS = 6_000;

  /**
   * How often the feed watchdog's scan runs. This is the client's only
   * always-on periodic wakeup added by EN-10628.
   *
   * 2s, and it stays 2s. I briefly moved this to 5s on battery grounds and that
   * was a bad trade: the RCA's Fix 10 chose 2s WITH a measurement behind it
   * (the tick quantises every decision, so a 5s tick gives back most of what the
   * acting table's 6s threshold buys - acting-table detection 6-12s rather than
   * 20-35s), whereas the battery saving was inferred, not measured. The work per
   * tick is a Map scan over at most four tables. If this is ever revisited it
   * needs an on-device measurement, not a judgement call: desktop numbers
   * mislead here (docs/development/game-client-performance.md).
   *
   * Note the watchdog's own unit tests pass `now` explicitly, so they pin the
   * THRESHOLDS and not the tick - a change here does not fail them.
   */
  private static FEED_WATCHDOG_TICK_MS = 2_000;
  /** Monotonic time of the last feed-driven forced reconnect. See escalateExhaustedTables. */
  private lastFeedEscalationAt = Number.NEGATIVE_INFINITY;
  /**
   * Feed-driven reconnects since the last time a table frame actually arrived.
   *
   * This is the load bound that matters, and it is a BUDGET rather than a rate
   * limit on purpose. A rate limit would mean a client whose backend is simply
   * down reconnects for as long as the outage lasts, which is the fleet-wide
   * self-inflicted load this platform has already been bitten by: a reconnect is
   * the most expensive thing a client can ask for ($connect with Descope JWKS
   * validation, then a resubscribeAll), and asking for it in a loop during an
   * outage adds load exactly when there is least capacity to absorb it.
   *
   * A budget instead says what is actually true: this remedy only ever fixes ONE
   * thing, a connection whose subscribes are being refused, and the fix is a new
   * connectionId. If a fresh connection also delivers nothing, the problem is not
   * this connection and another reconnect cannot help - so the client stops,
   * leaves it to the keepalive and the connection banner, and spends nothing
   * further until a frame proves it is healthy again (see noteTableFrame).
   *
   * Same reasoning, and the same shape, as useFeltHydrationRecovery's one-shot
   * guard: "even if the fresh connection STILL doesn't hydrate there is no
   * reconnect loop".
   */
  private feedEscalations = 0;
  private static MAX_FEED_ESCALATIONS = 2;
  /** When the pending escalation may fire (detection + jitter), or null. */
  private feedEscalationDueAt: number | null = null;
  // Long enough that a reconnect gets to prove itself (teardown delay, connect,
  // resubscribeAll, the post-reconnect grace and one repair) before the budget's
  // second attempt is allowed.
  private static FEED_ESCALATION_COOLDOWN_MS = 120_000;
  // Escalations are spread the same way and for the same reason the watchdog
  // spreads its repairs: clients that lose their feeds together (a broadcaster
  // hiccup) would otherwise reconnect on the same tick. The preceding repair is
  // already jittered, so this widens an existing spread rather than creating one.
  private static FEED_ESCALATION_JITTER_MS = 15_000;

  // ── Per-table subscription heartbeat ──
  //
  // Re-assert every open table's fan-out row on a fixed cadence, unconditionally,
  // whether or not anything looks wrong. This is what the Unity client does, and
  // it is why Unity does not show this class of bug: the server calls it out as
  // "`subscribeGame`-as-heartbeat (current Unity behavior)" in
  // shared/lib/dynamoHelpers.js, and notes the heartbeat is also what implicitly
  // refreshes the row's TTL via upsertGameConnection's put-with-fresh-TTL.
  //
  // React had no equivalent. It asserts each row ONCE, at subscribe time, and the
  // proxy's wire-level dedup (added in b15c1d4dd1 to stop App.tsx and Lobby.tsx
  // double-subscribing) makes a repeat SubscribeToGameUpdates for a tracked
  // gameID return without emitting - so nothing re-asserts it afterwards and any
  // loss is permanent for the life of the connection.
  //
  // Each table carries its OWN timer, started when that table was opened, so a
  // client's tables never beat together and a fleet's clients are staggered by
  // whenever their players happened to open each table.
  //
  // Cost, stated plainly because it is a real one: one subscribe per open table
  // per interval, always, healthy or not. At most four per interval per client,
  // with no burst shape to reason about. Per seated player that is the same rate
  // Unity generates today, since Unity's per-table sockets each beat for one
  // table - what changes is that React stops under-asserting. Against the
  // subscribe Lambda's measured p95 of 151ms and an observed ceiling of 70
  // concurrent with zero throttles, a fleet of ~1,235 connections averaging ~1.5
  // open tables sits near 185 req/s and ~28 concurrent. Four tables on every one
  // of those connections would sit AT the ceiling, so this interval is the dial
  // to turn if that ever becomes the real shape of the fleet.
  private heartbeatDueAt = new Map<string, number>();

  /**
   * Beats sent for this table since the last frame arrived on it.
   *
   * THE HEARTBEAT'S BUDGET. Every other remedy in this file is bounded for an
   * outage - the watchdog by a 60s per-table floor plus a 4-per-5-minutes
   * breaker, the escalation by MAX_FEED_ESCALATIONS - and the heartbeat was the
   * one that was not. On a fixed 10s cadence a four-table client sends 240
   * subscribes across a ten-minute outage, against 8 from the watchdog and 2
   * reconnects from the escalation. So during the exact window where capacity is
   * shortest, the unbounded mechanism dominates all the bounded ones by ~30x,
   * and each of those subscribes is an RDS checkout plus three queries plus a
   * DynamoDB transaction.
   *
   * Refilled ONLY by a real frame (see noteTableFrame), deliberately matching
   * `feedEscalations` rather than using a timer. The argument is the same one:
   * if beats are not producing frames then beating is not the remedy, and the
   * watchdog and the escalation already own that table under their own bounds.
   * A timer-based refill would just re-create the unbounded case more slowly.
   *
   * Note this costs nothing in the case the heartbeat exists for. A genuinely
   * evicted row makes the next subscribe NEW for this connection, so
   * `isNewSubscription` is true, the Lambda writes the row AND pushes a
   * snapshot - the FIRST beat answers it. The budget only bites when beats are
   * being refused, which is what it is for.
   */
  private heartbeatsSinceFrame = new Map<string, number>();
  private static MAX_HEARTBEATS_WITHOUT_FRAME = 3;
  private static SUBSCRIPTION_HEARTBEAT_MS = 10_000;
  private readonly heartbeatMs: number;
  /** Throttle on the foreground heartbeat pull-forward. See pullHeartbeatsForward. */
  private lastForegroundHeartbeat = 0;
  // A background long enough to have cost us a fan-out row without us noticing.
  // Deliberately small: `focus`/`visibilitychange` noise (DevTools, autocomplete
  // flicker, native pickers dismissing) is sub-second per the notes on
  // lastAwayAt, and anything past that could have been a real app suspend.
  //
  // It can afford to be small because a pull-forward is NOT new traffic - the
  // beat it moves was going to happen within one interval anyway. The throttle
  // below is what stops repeated toggles turning it into extra requests.
  private static MIN_AWAY_FOR_HEARTBEAT_PULL_MS = 2_000;
  private static FOREGROUND_HEARTBEAT_THROTTLE_MS = 5_000;

  /**
   * @param options Test seam only - production constructs this with no arguments.
   *   `feedWatchdog` overrides the per-table watchdog's bounds and `random` the
   *   escalation's jitter, so a test can pin timings that are deliberately
   *   randomised in the field.
   */
  constructor(
    options: {
      feedWatchdog?: TableFeedWatchdogOptions;
      random?: () => number;
      /** Heartbeat interval. 0 disables it - tests about the WATCHDOG use that
       *  so the two mechanisms can be exercised one at a time. */
      heartbeatMs?: number;
    } = {}
  ) {
    this.feedWatchdog = new TableFeedWatchdog(options.feedWatchdog);
    this.random = options.random ?? Math.random;
    this.heartbeatMs = options.heartbeatMs ?? ConnectionManager.SUBSCRIPTION_HEARTBEAT_MS;

    // Register lifecycle listeners for reconnection
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibility);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this.handleFocus);
      // Track "went away" so the foreground handler can distinguish a real
      // return-from-idle from a trivial focus toggle (see lastAwayAt docblock).
      window.addEventListener('blur', this.handleBlur);
    }
    this.setupCapacitorListener();

    // Sync socket.auth with utils/auth on every client-side refresh, AND
    // force a socket cycle so the server actually reads the new auth on the
    // very next handshake. Socket.io only reads `auth` at handshake time —
    // updating the client-side property after connection is a no-op until
    // the next reconnect happens on its own. The engine verifies only the
    // handshake access token and never refreshes it (EN-10961), so without
    // the cycle its copy expires. Brief reconnect blip on each rotation;
    // existing subscription-replay on reconnect handles table state.
    //
    // Cycle a connected socket. Reconnect one the engine refused (an expired
    // handshake, typically an app resuming after its token expired):
    // Socket.IO does not retry after a middleware rejection, so this refresh
    // is its only way back. Leave one that is still connecting or
    // auto-reconnecting alone — it reads the new auth on its next attempt,
    // and the very first connect during initialize() must not be disturbed.
    this.authListenerRemove = subscribeAuthChange(() => {
      const { access_token, refresh_token } = getAuthToken();
      if (!access_token || !refresh_token) return;

      // GUEST → MEMBER UPGRADE (EN-10549 cold-start follow-up).
      //
      // The bootstrap refresh deliberately leaves the app a guest for the round
      // trip, so initialize() can hand both sockets a GUEST handshake before the
      // refresh lands. The server then holds a receive-only observer connection
      // with no player identity: getPlayerMoney answers success:true with a
      // `guest:<connectionId>` GUID and "0.00" (balance renders $0) and
      // getPokerActions bails on the unmatched GUID (no action affordances) —
      // while table broadcasts keep arriving, so status stays 'connected' and the
      // feed watchdog never arms. Healthy and wrong; nothing downstream sees it.
      //
      // The per-socket `connected` gates below cannot repair this. They skip the
      // cycle entirely while a handshake is still in flight — exactly the window
      // the refresh lands in — and assigning `auth` never rewrites the
      // `?guest=true` already sent in the update socket's upgrade request.
      // Rebuild both sockets instead: reconnect() re-derives guest status at
      // connect() time and replays every tracked subscription.
      if (this.handshakeWasGuest && !isGuestSession()) {
        // Held while a location check is in flight. Clearing handshakeWasGuest
        // here is deliberate: the upgrade has been OBSERVED, and re-entering
        // this branch on a later auth change would queue it twice.
        if (this.geoHold) {
          console.log(
            '[ConnectionManager] Guest → member upgrade deferred — awaiting location verdict'
          );
          this.handshakeWasGuest = false;
          this.geoUpgradePending = true;
          return;
        }
        console.log(
          '[ConnectionManager] Guest → member upgrade — reconnecting both sockets as member'
        );
        this.handshakeWasGuest = false;
        this.reconnect('guest-upgrade');
        return;
      }

      if (this.actionSocket) {
        this.actionSocket.auth = { access_token, refresh_token };
        if (this.actionSocket.connected) {
          console.log(
            '[ConnectionManager] Auth changed — cycling action socket to sync engine cache'
          );
          this.actionSocket.disconnect();
          this.actionSocket.connect();
        } else if (!(this.actionSocket as any).active) {
          console.log('[ConnectionManager] Auth changed — reconnecting refused action socket');
          this.actionSocket.connect();
        }
      }
      if (this.updateSocket && (this.updateSocket as any).auth !== undefined) {
        // RawWebSocketAdapter's own `set auth` setter (RawWebSocketAdapter.tsx
        // lines ~568-574) cycles the connection when auth changes on a
        // live socket. Just assign — do NOT also call disconnect() here:
        // the adapter sets intentionalDisconnect=true on disconnect and
        // wouldn't auto-reconnect, and there's no matching connect(). The
        // earlier explicit disconnect was dead-by-timing code that got
        // skipped because `connected` is set async in onopen.
        (this.updateSocket as any).auth = { access_token, refresh_token };
      }
    });
  }

  // ── Public API ──

  get status() {
    return this._status;
  }

  onStatusChange(listener: (status: ConnectionStatus) => void) {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  /** Health of the path the player's actions travel on. See ActionPathStatus. */
  get actionPathStatus() {
    return this._actionPathStatus;
  }

  onActionPathStatusChange(listener: (status: ActionPathStatus) => void) {
    this.actionPathListeners.add(listener);
    return () => {
      this.actionPathListeners.delete(listener);
    };
  }

  /**
   * Returning to the foreground: make every open table due for a heartbeat now,
   * so the next tick re-asserts all of them.
   *
   * Why this exists. Three paths cover a return from background, and there was a
   * hole between them:
   *
   *   - sockets died while away -> the reconnect path re-subscribes everything;
   *   - away >= MIN_AWAY_FOR_FOREGROUND_RESUBSCRIBE_MS with live sockets -> the
   *     foreground resubscribeAll re-subscribes everything;
   *   - away for LESS than that with live sockets -> nothing re-asserted
   *     anything at all.
   *
   * That last case is a real exposure: the row can be evicted while the app is
   * suspended (a second device, a spurious 410, the same-player eviction in
   * upsertGameConnection) and the client comes back believing it is subscribed.
   * The heartbeat alone would find it, but only when that table's own clock next
   * came due - up to a full interval of blindness on a table the player is
   * looking at, which at the production turn clock can cost a turn.
   *
   * Pulling the clocks forward instead of calling resubscribeAll keeps this
   * cheap in the way that matters: it does not ADD a request, it moves one that
   * was already going to happen inside the next interval. So the sustained rate
   * is unchanged and only the timing moves. The throttle stops a player
   * flipping between apps from converting that into extra traffic.
   */
  private pullHeartbeatsForward(now: number, reason: string) {
    if (this.heartbeatMs <= 0) return;
    if (this.subscriptions.size === 0) return;
    const wall = Date.now();
    if (wall - this.lastForegroundHeartbeat < ConnectionManager.FOREGROUND_HEARTBEAT_THROTTLE_MS) {
      return;
    }
    this.lastForegroundHeartbeat = wall;
    console.log('[ConnectionManager] Foreground - re-asserting every table now', {
      reason,
      tables: this.subscriptions.size
    });
    this.heartbeatDueAt.forEach((_, gameID) => this.heartbeatDueAt.set(gameID, now));
  }

  /**
   * Push every table's heartbeat at least a full interval out, STAGGERED. Used
   * right after something has already asserted them all (a connect, the >=30s
   * foreground resubscribe) so the heartbeat does not pile straight onto that.
   *
   * The stagger matters. Each table's clock normally starts when that table was
   * joined, so a client's tables are naturally spread and never beat together.
   * Setting them all to the same `now + interval` threw that away: after any
   * reconnect all four tables became permanently synchronised and beat on one
   * tick for the rest of the session, turning a paced trickle into a burst of
   * four every interval. Spreading them back out over the following interval
   * restores the independence, and because each subsequent beat is scheduled
   * from when that table last fired, the stagger persists.
   *
   * Note the floor is still a full interval: the earliest table is due at
   * `now + interval`, so nothing lands on the assert this is deferring from.
   */
  private deferHeartbeats(now: number) {
    const count = this.heartbeatDueAt.size;
    if (count === 0) return;
    let index = 0;
    this.heartbeatDueAt.forEach((_, gameID) => {
      this.heartbeatDueAt.set(
        gameID,
        now + this.heartbeatMs + Math.round((index * this.heartbeatMs) / count)
      );
      index += 1;
    });
  }

  /**
   * Emit the due tables' subscription heartbeats.
   *
   * Driven from the feed watchdog's existing tick so this adds no timer of its
   * own - periodic wakeups are a measured battery cost on this client
   * (docs/development/game-client-performance.md).
   *
   * UPDATE socket only, via assertSubscriptionOnUpdateSocket: that is the leg the
   * fan-out row belongs to, and it refuses to put an empty playerGUID on the
   * wire, which matters because a replayed subscribe carrying '' makes the engine
   * remove this socket's table entry BEFORE it aborts - destructive rather than
   * merely useless (root cause 6).
   */
  private runSubscriptionHeartbeats(now: number) {
    if (this.heartbeatMs <= 0) return;
    this.subscriptions.forEach((sub, gameID) => {
      const dueAt = this.heartbeatDueAt.get(gameID);
      if (dueAt === undefined) {
        this.heartbeatDueAt.set(gameID, now + this.heartbeatMs);
        return;
      }
      if (now < dueAt) return;
      // Skip a table that has never produced a frame. A re-subscribe cannot
      // deliver a FIRST frame - the Lambda pushes a snapshot only for a
      // subscription that is new to this connection - so beating it every 10s
      // buys nothing and costs a full subscribe each time. Its clock still
      // advances, so it rejoins the rotation the moment it hydrates, and until
      // then it is hooks/useFeltHydrationRecovery's problem (a forced reconnect
      // gets a fresh connectionId, which IS a new subscription). Raised as an
      // asymmetry with the watchdog, which has always abstained here.
      if (!this.feedWatchdog.hasProvenFeed(gameID)) {
        this.heartbeatDueAt.set(gameID, now + this.heartbeatMs);
        return;
      }
      // A LIVE FEED NEEDS NO BEAT, and the beat stays PENDING rather than
      // deferring an interval.
      //
      // The fan-out row IS the delivery path, so frames arriving is proof the
      // row exists - and rewriting that row is the only thing a re-subscribe
      // does. So a beat on a table that is producing frames is not merely
      // probably unnecessary, it is provably useless. Unconditional beats put a
      // projected 5x on `subscribeGame` (RDS checkout + three queries + a
      // DynamoDB transaction each) for the whole session, which is the cost this
      // avoids: during a hand the measured frame cadence is p50 1005ms, so
      // almost every beat is skipped.
      //
      // NOT advancing the clock on a skip is what keeps repair latency intact.
      // Deferring a full interval would mean a row that dies just before a due
      // beat waits for the NEXT one - up to 10s WORSE than beating blindly.
      // Left pending, the beat re-evaluates on every 2s scan and fires as soon
      // as the feed goes quiet, so the trigger becomes "this table went silent"
      // rather than "10s elapsed". The only case that loses anything is a row
      // dying within FEED_LIVE_WITHIN_MS of an already-due beat, which waits one
      // extra scan.
      const silentForMs = this.feedWatchdog.msSinceLastFrame(gameID, now);
      if (silentForMs !== null && silentForMs < ConnectionManager.FEED_LIVE_WITHIN_MS) {
        return;
      }
      // STAND DOWN when beats are demonstrably not working. See
      // heartbeatsSinceFrame - this is what bounds the heartbeat across an
      // outage, the way the breaker bounds the watchdog and the budget bounds
      // the escalation.
      const sent = this.heartbeatsSinceFrame.get(gameID) ?? 0;
      if (sent >= ConnectionManager.MAX_HEARTBEATS_WITHOUT_FRAME) {
        // Once per stand-down, not per tick: this is the steady state for the
        // rest of the outage. The extra increment is the "already logged" mark,
        // the same trick escalateExhaustedTables uses.
        if (sent === ConnectionManager.MAX_HEARTBEATS_WITHOUT_FRAME) {
          this.heartbeatsSinceFrame.set(gameID, sent + 1);
          console.warn(
            '[ConnectionManager] heartbeat produced no frame - standing down for this table, leaving it to the watchdog',
            { gameID, beats: sent, silentForMs: Math.round(silentForMs ?? 0) }
          );
        }
        return;
      }
      // Advance from NOW rather than from dueAt: after a long background stretch
      // the old deadline can be many intervals behind, and stepping it forward
      // one interval at a time would fire a burst of catch-up beats for a table
      // that only needs one.
      this.heartbeatDueAt.set(gameID, now + this.heartbeatMs);
      this.heartbeatsSinceFrame.set(gameID, sent + 1);
      this.assertSubscriptionOnUpdateSocket(sub);
    });
  }

  /**
   * Tell the feed watchdog which table the player is acting on, so that table's
   * feed is judged on a much tighter clock (see TableFeedWatchdog.setPriority).
   *
   * Called by the action row, which is the only thing that knows both that it is
   * our turn AND that this is the panel the player is looking at. Pass null when
   * the turn ends.
   */
  setPriorityTable(gameID: string | number | null) {
    this.feedWatchdog.setPriority(gameID);
  }

  /**
   * Re-assert this client's subscription to one table on the UPDATE socket - the
   * socket its frames actually arrive on.
   *
   * Exposed because the component that submits a move is the only thing that
   * knows the move produced no effect (see utils/actionOutcome), and a dead feed
   * for that one table is the likelier explanation than a dead send. Safe to
   * call on demand: if the table's fan-out row is missing the subscribe Lambda
   * writes it and pushes a targeted fresh snapshot; if it already exists the
   * call is a no-op on the server. Nothing else is touched - in particular it
   * never reconnects, which would blank every table to fix one.
   */
  resyncTable(gameID: string | number) {
    const sub = this.subscriptions.get(String(gameID));
    if (!sub) return;
    // A LIVE FEED CANNOT BE REPAIRED BY RE-SUBSCRIBING.
    //
    // The caller (PokerActions' recovery) cannot tell a dropped action from one
    // whose confirming frame is merely late: `actionAppearsDropped` compares the
    // turn identity the client currently holds, which on a slow link is simply
    // behind. So on a healthy-but-slow table it asked for a re-assert roughly
    // every 4.5s for the whole turn - measured 4 in 20s - and every one of them
    // was pure cost: a re-subscribe is a MySQL pool connection plus three
    // queries and two DynamoDB ops on the server, and on the client it opens a
    // snap window that suppresses this table's animation.
    //
    // Frames arriving IS proof the fan-out row exists and this connection is on
    // it, which is the only thing a re-subscribe writes. So gate on it. A table
    // that has never framed (null) is exactly the unhydrated case the repair was
    // built for, so it proceeds.
    const silentForMs = this.feedWatchdog.msSinceLastFrame(String(gameID), monoNow());
    if (silentForMs !== null && silentForMs < ConnectionManager.FEED_LIVE_WITHIN_MS) {
      console.log(
        '[ConnectionManager] Skipping re-assert: feed is live for table',
        sub.gameID,
        `(last frame ${Math.round(silentForMs)}ms ago)`
      );
      return;
    }
    console.log('[ConnectionManager] Re-asserting feed subscription for table', sub.gameID);
    // Flag the resync window. A repair that finds the fan-out row MISSING makes
    // the Lambda write it and push a targeted snapshot, and nothing downstream
    // can tell that snapshot apart from live play: the burst momentarily
    // unmatches the player (seatId -1) before the seated frame lands, so the
    // seat ring rotates away-and-back and chips/board re-animate. On the held-
    // action path that lands at the exact moment the player is waiting on their
    // move. markTableResyncing() is what SeatRotationContext and tableSnapState
    // consult to keep a background resync silent; reconnect() and the foreground
    // resubscribe already set it, these two repair paths did not.
    //
    // Deliberately NOT inside assertSubscriptionOnUpdateSocket, which the 10s
    // heartbeat also calls: a 6s window re-armed every 10s would suppress
    // GENUINE seat rotations most of the time. Only the evidence-driven repairs
    // set it, and they are rare.
    //
    // Scoped to THIS table. A single table's repair used to open a module-wide
    // window and so froze animation on every other open table too - see
    // utils/tableResyncState.
    markTableResyncing(sub.gameID);
    this.assertSubscriptionOnUpdateSocket(sub);
  }

  /**
   * Initialize sockets based on environment config.
   * Called from the React provider after gameType is set.
   */
  initialize(gameType: string) {
    const isDual = !!UPDATE_SOCKET_URL;
    this.mode = isDual
      ? 'dual'
      : SOCKET_SERVER_URL.includes('ws.') || SOCKET_SERVER_URL.includes('wss://ws')
      ? 'single-ws'
      : 'single-io';

    console.log('[ConnectionManager] Initializing, mode:', this.mode, 'gameType:', gameType);
    this.connect();
  }

  /**
   * Connect both sockets with fresh tokens.
   */
  connect() {
    if (this.destroyed) return;

    const token = getAuthToken();
    const guest = isGuestSession();
    console.log(
      '[ConnectionManager] Connecting with token:',
      !!token.access_token,
      'guest:',
      guest
    );

    if (!token.access_token && !guest) {
      console.log('[ConnectionManager] No token available — skipping connect');
      return;
    }

    this.setStatus('connecting');
    // Record the identity we are about to handshake with — the auth listener
    // needs it to spot a guest→member upgrade that a live `auth` assignment
    // cannot repair.
    this.handshakeWasGuest = guest;

    // Guests connect like a normal dual session: the action (engine Socket.IO)
    // socket carries the lobby broadcast, and the update (API Gateway WS) socket
    // carries per-table / tournament observation. Both sockets handshake with a
    // guest marker ({guest:true} / ?guest=true) instead of a token.
    if (this.mode === 'dual') {
      this.connectActionSocket(token);
      this.connectUpdateSocket(token);
    } else if (this.mode === 'single-ws') {
      this.singleSocket = this.createRawSocket(SOCKET_SERVER_URL, token);
      this.setupSocketListeners(this.singleSocket);
    } else if (this.mode === 'single-io') {
      this.singleSocket = this.createIOSocket(SOCKET_SERVER_URL, token) as any;
      this.setupIOListeners(this.singleSocket as any as IOSocket);
    }
  }

  /**
   * Force reconnect both sockets with fresh tokens.
   */
  /**
   * Hold or release the guest-to-member upgrade.
   *
   * Releasing performs any upgrade that arrived while held. Releasing when none
   * did is a no-op, so the caller does not have to track whether one happened --
   * which matters because the caller (the location gate) has no visibility of
   * the socket's lifecycle.
   *
   * The gate is responsible for always releasing, including on failure. It
   * carries its own failsafe for that; see Lobby's GEO_HOLD_FAILSAFE_MS.
   */
  setGeoHold(hold: boolean, admitted = true) {
    if (this.geoHold === hold) return;
    this.geoHold = hold;
    if (hold) return;

    if (!this.geoUpgradePending) return;
    this.geoUpgradePending = false;

    // A REFUSED PLAYER MUST NOT GET THE UPGRADE PERFORMED FOR THEM.
    //
    // `reconnect()` schedules `connect()` 500ms later and nothing cancels that
    // timer. Releasing unconditionally therefore scheduled a member handshake
    // the verdict had just refused: App's disconnect effect fires on
    // `isLocationInvalid`, tears the socket down, and then the already-queued
    // callback connects again -- resuming the rejected-handshake churn this
    // hold exists to stop.
    //
    // Dropping the pending upgrade is better than cancelling the timer after
    // the fact, because it never starts. The player keeps the guest socket they
    // already had, which is the correct state for someone who is being refused.
    if (!admitted) {
      console.log('[ConnectionManager] Location verdict refused — dropping the deferred upgrade');
      return;
    }

    console.log('[ConnectionManager] Location verdict in — performing deferred upgrade');
    this.reconnect('guest-upgrade');
  }

  /**
   * @param reason why, for the connection_lost guardrail event (EN-11182):
   *   which repair or upgrade forced a new connection. `requested` is a caller
   *   outside this class (the context's `reconnect`).
   */
  reconnect(reason = 'requested') {
    if (this.destroyed) return;
    console.log('[ConnectionManager] Reconnecting...');
    // The fresh snapshot this reconnect pulls is a BACKGROUND resync of tables the
    // player is already viewing — flag it so the seat-rotation layer snaps to the
    // new state instead of replaying the entrance rotation ("reload" flicker).
    // Client-wide, because a reconnect really does re-hydrate every table.
    markAllTablesResyncing();
    // The reason applies to this status change only; when already not connected
    // it is not a drop, and must not be pinned on a later one.
    this.nextDropReason = reason;
    this.setStatus('reconnecting');
    this.nextDropReason = null;

    // Tear down existing connections
    this.disconnectSockets();

    // Small delay to let cleanup complete
    setTimeout(() => {
      this.connect();
    }, 500);
  }

  /**
   * A table snapshot (gotTexas/gotOmaha) arrived — the felt is (re)hydrating, so
   * the foreground staleness gate is satisfied; cancel any pending reconnect.
   * Arrow fn so it can be passed directly as a socket listener.
   */
  private noteFeltUpdate = () => {
    if (this.feltResyncTimer) {
      clearTimeout(this.feltResyncTimer);
      this.feltResyncTimer = null;
    }
  };

  /**
   * After a foreground re-subscribe on live sockets, wait briefly for the felt to
   * re-hydrate (a gotTexas/gotOmaha). If none arrives, force a full reconnect so a
   * fresh connectionId makes the server treat it as a NEW subscription and push
   * the snapshot. See the feltResyncTimer field note.
   */
  private armFeltStalenessGate() {
    if (this.destroyed) return;
    if (this.feltResyncTimer) clearTimeout(this.feltResyncTimer);
    // 4s base + up to 3s random jitter so a fleet of clients foregrounding at the
    // same moment (e.g. after a server blip) doesn't reconnect in lockstep.
    const delay = 4000 + Math.floor(Math.random() * 3000);
    this.feltResyncTimer = setTimeout(() => {
      this.feltResyncTimer = null;
      // Nothing to re-hydrate if we've torn down or left every table (lobby).
      if (this.destroyed || this.subscriptions.size === 0) return;
      // Only escalate to a reconnect when a table the player is actually viewing
      // never hydrated (blank felt / "spinning of death"). A live table between
      // hands is silent on gotTexas for many seconds (only gotTnmStatus ticks) —
      // reconnecting that healthy table needlessly blanks the felt and pops the
      // "Exit Table" modal. See utils/tableHydrationState.
      if (!hasUnhydratedTable()) {
        console.log(
          '[ConnectionManager] Foreground re-subscribe: felt quiet but hydrated — no reconnect'
        );
        return;
      }
      console.log(
        '[ConnectionManager] Foreground re-subscribe did not re-hydrate the felt — forcing reconnect'
      );
      this.reconnect('foreground-unhydrated');
    }, delay);
  }

  /**
   * Disconnect everything.
   */
  disconnect() {
    // Deliberate (logout, teardown): not a dropped connection. Held across the
    // socket teardown, which can recompute the status on its own.
    this.nextDropReason = DELIBERATE_DISCONNECT;
    this.disconnectSockets();
    this.setStatus('disconnected');
    this.nextDropReason = null;
  }

  /**
   * Clean up everything — called on unmount.
   */
  destroy() {
    this.destroyed = true;
    if (this.resubscribeTimer) {
      clearTimeout(this.resubscribeTimer);
      this.resubscribeTimer = null;
    }
    if (this.feltResyncTimer) {
      clearTimeout(this.feltResyncTimer);
      this.feltResyncTimer = null;
    }
    this.disconnectSockets();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibility);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', this.handleFocus);
      window.removeEventListener('blur', this.handleBlur);
    }
    if (this.appListenerRemove) {
      this.appListenerRemove();
    }
    if (this.authListenerRemove) {
      this.authListenerRemove();
      this.authListenerRemove = null;
    }
    this.stopFeedWatchdog();
    this.statusListeners.clear();
    this.actionPathListeners.clear();
    this.subscriptions.clear();
    this.tournamentSubscriptions.clear();
  }

  /**
   * Track a table subscription for auto-resubscribe on reconnect.
   *
   * `playerGUID` is taken from the CALLER'S payload, falling back to localStorage
   * only when the caller did not supply one. It used to read localStorage
   * unconditionally, and an empty result there is actively destructive rather
   * than merely useless: the engine's `subscribeToGame` removes this socket's
   * existing table entry and only THEN checks the GUID, returning early on an
   * empty one before it re-adds the entry (apps/engine/messages/inbound.js). So a
   * replayed subscribe carrying `playerGUID: ''` deregisters a player who was
   * correctly registered a moment earlier, and tells nobody. Prefer the value the
   * caller already proved it had.
   */
  trackSubscription(
    gameID: string | number,
    gameType: string,
    playerGUID?: string,
    username?: string
  ) {
    // Normalised, because the callers are not. Every caller reaches this through
    // the untyped socket proxy with `data.gameID`, which is a number on some
    // paths and a string on others, and TypeScript cannot see it. A numeric key
    // here would still LOOK tracked while the watchdog (which keys by
    // `String(gameID)`) and resyncTable both miss it - a table quietly outside
    // every repair path, with nothing to log about it. Raised in PR review as
    // the one assumption the watchdog cannot detect breaking.
    const key = String(gameID);
    this.subscriptions.set(key, {
      gameID: key,
      gameType,
      playerGUID: playerGUID || localStorage.getItem('playerGUID') || '',
      username: username || localStorage.getItem('playerName') || ''
    });
    this.feedWatchdog.track(key);
    // The table's own heartbeat clock starts when the table is opened, which is
    // what staggers a client's tables against each other without needing jitter.
    if (!this.heartbeatDueAt.has(key)) {
      this.heartbeatDueAt.set(key, monoNow() + this.heartbeatMs);
    }
  }

  /**
   * Remove a table subscription.
   */
  untrackSubscription(gameID: string | number) {
    const key = String(gameID);
    this.subscriptions.delete(key);
    this.feedWatchdog.untrack(key);
    this.heartbeatDueAt.delete(key);
    this.heartbeatsSinceFrame.delete(key);
  }

  /**
   * Get the socket proxy — same interface as DualSocketContext's socket.
   * Routes emits to the correct socket based on event type.
   */
  getProxy(): any {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const getActionTarget = () => (self.mode === 'dual' ? self.actionSocket : self.singleSocket);

    return {
      emit: (event: string, data?: any, callback?: any) => {
        // Track subscriptions
        if (event === 'SubscribeToGameUpdates' && data?.gameID) {
          // Wire-level dedup for SubscribeToGameUpdates.
          //
          // Two independent code paths auto-subscribe seated tables:
          //   • App.tsx GOT_PLAYER_GAMES handler   — its own `subscribedThisSession` map
          //   • Lobby.tsx LOBBY_DATA_RECEIVED     — its own `subscribedThisLobbyMount` ref
          // Neither knows about the other. The engine's 20 s texas freshness
          // filter drops texas from `got_playerGames` while keeping omaha, so
          // the two handlers overlap ONLY on omaha — reproducing the reported
          // "omaha subscribes twice on lobby load, texas once" wire trace.
          // Dedup here (at the single point every subscribe passes through)
          // instead of trying to keep the two handler-local maps in sync.
          // `resubscribeAll` bypasses this by emitting directly to the
          // underlying sockets, because after a reconnect the server has lost
          // the subscription and MUST be re-emitted even though the entry is
          // still in `this.subscriptions`.
          //
          // A duplicate is dropped on purpose and stays dropped (EN-10628 looked
          // at narrowing this so duplicates reached the engine, and withdrew
          // it: the engine's per-table registration is not in the path table
          // frames take to this client, so re-asserting it repairs nothing).
          // Repairing a table whose feed has died is the feed watchdog's job
          // (see resyncTable), and it targets the socket the feed lives on.
          const gid = String(data.gameID);
          const requestedGame = data.game || data.gameType || 'texas';
          const existing = self.subscriptions.get(gid);
          const fillsEmptyGuid = !!existing && !existing.playerGUID && !!data.playerGUID;
          if (existing && existing.gameType === requestedGame && !fillsEmptyGuid) {
            return undefined;
          }
          if (fillsEmptyGuid) {
            // Do NOT dedupe this one. The tracked entry was created by an
            // earlier subscribe that carried no GUID, and everything downstream
            // refuses to use it: the Lambda requires a GUID, `resubscribeAll`
            // and the targeted re-assert both skip an empty one, and the feed
            // watchdog ignores a table that never produced a frame. So this is
            // the FIRST subscribe for this table that can actually establish a
            // fan-out row - and deduping it left the table permanently dead
            // while Fix 6 claimed "the view layer's own subscribe will
            // re-establish it". This is that subscribe (PR review finding 4).
            console.log(
              '[ConnectionManager] Subscribe now carries a playerGUID for table',
              gid,
              '- letting it through the dedup'
            );
          }
          self.trackSubscription(data.gameID, requestedGame, data.playerGUID, data.username);
        }
        if (event === 'UnsubscribeFromGameUpdates' && data?.gameID) {
          self.untrackSubscription(data.gameID);
        }
        // Track tournament (gotTnmStatus) subscriptions so they're replayed on
        // reconnect too — see the tournamentSubscriptions field note.
        //
        // Reject anything that isn't a positive-integer tournamentId here BEFORE
        // it hits the wire OR the tracked set. `-1` is the backend sentinel
        // for "no tournament" (broadcastTournamentDetailsStatus's empty-DB path)
        // and is truthy in JS — the old `if (data?.tournamentId)` guard let it
        // through, creating dead subscribeTournament(-1) writes to DynamoDB
        // under TOURNAMENT#-1 AND polluting resubscribeAll's replay set so every
        // reconnect re-emitted the same garbage. Blocking here also short-circuits
        // the socket emit below, so no wire traffic for the bad ID.
        if (event === 'subscribeTournament') {
          if (!isValidTournamentId(data?.tournamentId)) {
            // Don't silently swallow — surface the (deduped) warning so an id
            // arriving from an unfiltered upstream is visible in the console.
            // Standards: "Error Handling — define normal flows and use proper
            // error handling rather than ignoring them."
            warnBadTournamentId('connectionManager:subscribeTournament', data?.tournamentId);
            return undefined;
          }
          self.tournamentSubscriptions.add(String(data.tournamentId));
        }
        if (event === 'unsubscribeTournament') {
          if (!isValidTournamentId(data?.tournamentId)) {
            warnBadTournamentId('connectionManager:unsubscribeTournament', data?.tournamentId);
            return undefined;
          }
          self.tournamentSubscriptions.delete(String(data.tournamentId));
        }
        if (event === ENGINE_ONLINE_EVENT) {
          self.rememberOnlinePlayer(data?.playerGUID);
        }

        if (self.mode === 'dual') {
          // Dual-emit (subscribe/unsubscribe) — needs to reach both the
          // legacy engine (subscriber bookkeeping and the idle-table wake-up)
          // and the API Gateway lambda (for DynamoDB subscriber row). Each
          // destination processes its delivery exactly once.
          if (DUAL_EMIT_EVENTS.includes(event)) {
            // A DOWN LEG IS SKIPPED FOR THE VOTE, AND ONLY FOR THE VOTE.
            //
            // `emit` on a disconnected socket does not fail — both transports
            // BUFFER it (socket.io in `sendBuffer`, RawWebSocketAdapter in its
            // uncapped untimestamped `messageQueue`, see `timeout().emit` below)
            // and flush whenever the socket returns.
            //
            // For `vote_MultiRun` that is a correctness bug, not a latency one:
            // the payload is `{gameID, game, data:'&vote=N'}` with NO hand
            // identity, and both writers authorise it against whatever window is
            // open WHEN IT ARRIVES. A vote buffered on a down leg during hand N
            // lands as a vote in hand N+1's window — a 1X decline the player
            // never made, for a pot they are now contesting. `canDeliver` already
            // reports the vote sendable while EITHER leg is up, so the down leg's
            // copy is pure surplus and dropping it loses nothing.
            //
            // The subscribe pair is deliberately NOT gated. Those are idempotent
            // and self-correcting — a late one re-asserts a subscription that is
            // still wanted — and buffering them before the first handshake is a
            // path the app relies on today.
            const skipQueued = event === 'vote_MultiRun';
            if (!skipQueued || self.actionSocket?.connected) {
              self.actionSocket?.emit(event, data, callback);
            }
            if (!skipQueued || self.updateSocket?.connected) {
              self.updateSocket?.emit(event, data, callback);
            }
            return;
          }
          if (SERVERLESS_EMIT_EVENTS.includes(event) || GAME_UPDATE_EVENTS.includes(event)) {
            return self.updateSocket?.emit(event, data, callback);
          }
          return self.actionSocket?.emit(event, data, callback);
        }
        return (self.singleSocket as any)?.emit(event, data, callback);
      },

      on: (event: string, handler: any) => {
        if (self.mode === 'dual') {
          if (DUAL_LISTEN_EVENTS.includes(event)) {
            self.actionSocket?.on(event, handler);
            self.updateSocket?.on(event, handler);
            return;
          }
          if (GAME_UPDATE_EVENTS.includes(event)) {
            return self.updateSocket?.on(event, handler);
          }
          return self.actionSocket?.on(event, handler);
        }
        return (self.singleSocket as any)?.on(event, handler);
      },

      /**
       * Remove a consumer's listener(s).
       *
       * NOTE the handler-less form deletes EVERY listener for that event on the
       * underlying socket, including this manager's own. `App.tsx`, `Lobby.tsx`
       * and `GuestLobby.tsx` use it that way in effect cleanups (38 call sites).
       * That is why the manager must not rely on `socket.on(...)` for its own
       * bookkeeping - it uses the adapter's `addTap()` channel instead, which no
       * consumer can clear.
       *
       * Narrowing this to "remove only the handlers the consumer registered" was
       * implemented and REVERTED (EN-10628, PR review finding 2). It is the more
       * correct semantic, but those blanket removals have been suppressing this
       * manager's own action-socket lifecycle handlers for a long time, and
       * restoring them all at once revives two paths with real blast radius:
       *
       *   - `connect_error` reaches `handleAuthError`, which on anything that
       *     looks like a 401/403 can `clearAuth()` + `hardReload()`. A transient
       *     engine Unauthorized would log a seated member out mid-hand.
       *   - `disconnect` calls `socket.connect()` directly on `transport close`,
       *     bypassing the configured Socket.IO backoff (2s, max 10s). On a flaky
       *     radio that is a reconnect loop.
       *
       * Fixing this properly means auditing those handlers one at a time with a
       * device test, not flipping them all live inside an unrelated change.
       *
       * What it actually costs, precisely (an earlier version of this comment
       * overstated it as "the trigger is inert in dual mode", which is wrong).
       * Of the two handlers that publish action-path status, only `disconnect`
       * is swallowed - App passes a handler to `off('connect', handleConnect)`,
       * so the manager's `connect` handler survives and still publishes 'ok'.
       * And `disconnectSockets()` publishes 'down' directly. So the down -> ok
       * transition DOES fire on every manager-initiated reconnect (foreground
       * resync, a keepalive-forced reconnect), and PokerActions' re-send trigger
       * works there. What stays blind is an UNSOLICITED action-socket drop: no
       * 'down' is published, so the recovery that follows it is not a
       * transition. The ack-timeout and no-effect triggers cover that case.
       */
      off: (event: string, handler?: any) => {
        if (self.mode === 'dual') {
          if (DUAL_LISTEN_EVENTS.includes(event)) {
            self.actionSocket?.off(event, handler);
            self.updateSocket?.off(event, handler);
            return;
          }
          if (GAME_UPDATE_EVENTS.includes(event)) {
            return self.updateSocket?.off(event, handler);
          }
          return self.actionSocket?.off(event, handler);
        }
        return (self.singleSocket as any)?.off(event, handler);
      },

      get connected() {
        if (self.mode === 'dual') {
          return self.actionSocket?.connected ?? false;
        }
        return (self.singleSocket as any)?.connected ?? false;
      },

      get id() {
        if (self.mode === 'dual') {
          return self.actionSocket?.id || self.updateSocket?.id;
        }
        return (self.singleSocket as any)?.id;
      },

      get auth() {
        return self.actionSocket?.auth || (self.singleSocket as any)?.auth;
      },

      set auth(value: any) {
        if (self.actionSocket) (self.actionSocket as any).auth = value;
        if (self.updateSocket) (self.updateSocket as any).auth = value;
        if (self.singleSocket) (self.singleSocket as any).auth = value;
      },

      get io() {
        return self.actionSocket?.io || (self.singleSocket as any)?.io;
      },

      connect: () => self.connect(),
      disconnect: () => self.disconnect(),

      get volatile() {
        return {
          emit: (event: string, data?: any, callback?: any) => {
            return (getActionTarget() as any)?.volatile?.emit(event, data, callback);
          }
        };
      },

      // `socket.timeout(ms).emit(...)` — the shape every player action uses
      // (GameService's fold/check/call/raise/allIn all go through here).
      //
      // The optional chaining used to swallow the whole call: with no action
      // socket, `(self.actionSocket as any)?.timeout(ms)` short-circuits to
      // undefined, the emit never happens, and — the part that actually hurt —
      // the caller's callback is NEVER INVOKED. Socket.IO's ack timeout can't
      // fire for an emit that was never made, so the UI got neither an ack nor a
      // timeout: no "taking longer than expected" banner, no unlock, and the
      // action row sat dead until its backstop expired. A tap that goes nowhere
      // must at minimum report that it went nowhere, so synthesise the timeout
      // callback here in the caller's own error shape (a truthy first argument;
      // see GameService's ackCallbackWithTimeout).
      // A DISCONNECTED socket is refused too, not just a missing one.
      //
      // The existence check alone was not enough (PR review). Every transport
      // here BUFFERS an emit made while disconnected, and the buffer has no
      // concept of a poker turn:
      //
      //  - socket.io (dual / single-io) queues into `sendBuffer`. Its own 2s ack
      //    timer usually rescues this - on expiry it splices the packet out and
      //    reports the timeout - but a reconnect INSIDE that 2s window flushes
      //    the action, the returning ack clears the timer, and the player is told
      //    it succeeded for a turn that may have moved on.
      //  - RawWebSocketAdapter (single-ws) is worse: `timeout()` returns `this`,
      //    so this shim's shape check passes, the emit lands in an untimestamped
      //    uncapped `messageQueue` that survives reconnect, NO ack timer exists,
      //    and on flush it calls `callback(null)` - which ackCallbackWithTimeout
      //    reads as SUCCESS. An affirmative false confirmation for a stale move.
      //
      // Refusing here is strictly safer than buffering, because the caller's
      // recovery is turn-aware and a buffer is not: PokerActions re-sends only
      // after `actionAppearsDropped` confirms the turn has not moved
      // (utils/actionOutcome), whereas a buffer flush delivers whatever it holds
      // whenever the socket returns. So this converts an unguarded late delivery
      // into a guarded re-send.
      //
      // FAILS CLOSED on a transport that does not report `connected`: on a
      // money-moving emit, "cannot confirm the socket is up" should route to the
      // turn-aware re-send path rather than risk the buffer described above.
      // Both real transports expose the property, so this only differs for a
      // mock. (This branch had `=== false`; main's polarity wins.)
      timeout: (ms: number) => ({
        emit: (event: string, data?: any, callback?: any) => {
          const target = getActionTarget();
          if (!target?.connected) {
            callback?.(new Error('Action socket unavailable'));
            return undefined;
          }
          return (target as any).timeout(ms).emit(event, data, callback);
        }
      }),

      /**
       * Can an emit of `event` actually reach a server right now?
       *
       * The `connected` getter above answers for the ACTION socket only, which
       * is the wrong answer for anything the routing tables send elsewhere: a
       * dual-emit event (`vote_MultiRun`) still lands if either leg is up, and a
       * serverless event needs the update socket specifically. So the liveness
       * question is asked of the same socket the emit would pick — the branches
       * below mirror `emit`'s, deliberately, rather than introducing a second
       * routing list to keep in sync.
       *
       * Omitting `event` asks about the action path, which is where an
       * unclassified event goes.
       *
       * This is what `timeout().emit` already does inline for player actions
       * (refuse rather than let a transport buffer the packet); exposing it
       * lets a plain `emit` caller make the same check, since a plain emit has
       * no ack to fail on. Callers opt in — see GameService's
       * rejectUnsendableTableCommand.
       */
      canDeliver: (event?: string) => {
        if (self.mode !== 'dual') return (self.singleSocket as any)?.connected === true;
        const actionUp = self.actionSocket?.connected === true;
        const updateUp = self.updateSocket?.connected === true;
        if (event && DUAL_EMIT_EVENTS.includes(event)) return actionUp || updateUp;
        if (
          event &&
          (SERVERLESS_EMIT_EVENTS.includes(event) || GAME_UPDATE_EVENTS.includes(event))
        ) {
          return updateUp;
        }
        return actionUp;
      },

      once: (event: string, handler: any) => {
        if (GAME_UPDATE_EVENTS.includes(event)) {
          return self.updateSocket?.once(event, handler);
        }
        return (
          self.actionSocket?.once(event, handler) ||
          (self.singleSocket as any)?.once(event, handler)
        );
      },

      removeAllListeners: (event?: string) => {
        if (self.mode === 'dual') {
          if (event && GAME_UPDATE_EVENTS.includes(event)) {
            return self.updateSocket?.removeAllListeners(event);
          } else if (event) {
            return self.actionSocket?.removeAllListeners(event);
          }
          self.actionSocket?.removeAllListeners();
          self.updateSocket?.removeAllListeners();
        } else {
          (self.singleSocket as any)?.removeAllListeners(event);
        }
      },

      removeListener: (event: string, handler?: any) => {
        if (GAME_UPDATE_EVENTS.includes(event)) {
          return self.updateSocket?.removeListener(event, handler);
        }
        return (
          self.actionSocket?.removeListener(event, handler) ||
          (self.singleSocket as any)?.removeListener?.(event, handler)
        );
      },

      listeners: (event: string) => {
        if (GAME_UPDATE_EVENTS.includes(event)) {
          return self.updateSocket?.listeners(event) || [];
        }
        return (
          self.actionSocket?.listeners(event) ||
          (self.singleSocket as any)?.listeners?.(event) ||
          []
        );
      },

      hasListeners: (event: string) => {
        if (GAME_UPDATE_EVENTS.includes(event)) {
          return self.updateSocket?.hasListeners(event) || false;
        }
        return (
          self.actionSocket?.hasListeners(event) ||
          (self.singleSocket as any)?.hasListeners?.(event) ||
          false
        );
      },

      compress: (value: boolean) => ({
        emit: (event: string, data?: any, callback?: any) => {
          return (getActionTarget() as any)?.compress(value)?.emit(event, data, callback);
        }
      }),

      // Direct access
      actionSocket: self.actionSocket,
      updateSocket: self.updateSocket
    };
  }

  // ── Private methods ──

  private setStatus(status: ConnectionStatus) {
    if (status === this._status) return;
    const wasConnected = this._status === 'connected';
    this._status = status;
    if (wasConnected) this.recordConnectionLost();
    this.statusListeners.forEach((l) => l(status));
  }

  /**
   * EN-11182: the connection left `connected`. Why: the reason reconnect() was
   * given, or `transport` when a socket dropped on its own. A deliberate
   * disconnect is not reported. Never throws: analytics must not break state.
   */
  private recordConnectionLost() {
    const reason = this.nextDropReason ?? 'transport';
    this.nextDropReason = null;
    if (reason === DELIBERATE_DISCONNECT) return;
    this.connectionDrops += 1;
    try {
      reportConnectionLost(localStorage.getItem('playerGUID') || '', reason, this.connectionDrops);
    } catch {
      // Storage or analytics unavailable: the drop simply goes unreported.
    }
  }

  private setActionPathStatus(status: ActionPathStatus) {
    if (status === this._actionPathStatus) return;
    this._actionPathStatus = status;
    this.actionPathListeners.forEach((l) => l(status));
  }

  /**
   * Publish the action path's health from the socket's own state.
   *
   * Wired into BOTH modes. It used to be called only from the dual-mode action
   * socket's connect/disconnect handlers, so in single-socket mode
   * `_actionPathStatus` never left its initial 'down' for the whole session -
   * which silently killed Fix 3's "socket came back" re-send trigger there and
   * would have made any future consumer of this field wrong in that mode.
   * Raised in PR review.
   */
  private recomputeActionPathStatus() {
    const socket: any = this.mode === 'dual' ? this.actionSocket : this.singleSocket;
    this.setActionPathStatus(socket?.connected ? 'ok' : 'down');
  }

  /**
   * Emit one table subscription on the UPDATE socket only - the leg that owns
   * this table's fan-out row and therefore its frames.
   *
   * Mirrors the payload `resubscribeAll` replays. Skips the engine leg on
   * purpose: the engine's per-table registration is not in the path frames take
   * to this client (its own fan-out is dead code), so re-asserting it costs four
   * engine queries and repairs nothing. Refuses to send an empty `playerGUID`;
   * the Lambda requires one and the engine treats it as a reason to abort after
   * it has already removed this socket's entry (see trackSubscription).
   */
  private assertSubscriptionOnUpdateSocket(sub: Subscription) {
    if (!sub) return;
    if (!sub.playerGUID) {
      console.warn(
        '[ConnectionManager] Refusing to assert subscription with empty playerGUID for table',
        sub.gameID
      );
      return;
    }
    const payload = buildSubscribePayload(sub);
    const target: any = this.mode === 'dual' ? this.updateSocket : this.singleSocket;
    target?.emit('SubscribeToGameUpdates', payload);
  }

  /**
   * A table frame arrived on the update socket. Feeds the watchdog so the table
   * is known to be alive. Frames are uncompressed on this socket and carry
   * `game.gameID`, so this costs nothing beyond a property read.
   */
  /** Adapter tap: forward table frames to the feed watchdog. Stable identity. */
  private tapTableFrames = (event: string, data: any) => {
    if (event === 'gotTexas' || event === 'gotOmaha') this.noteTableFrame(data);
  };

  private noteTableFrame = (data: any) => {
    this.noteFeltUpdate();
    const gameID = data?.game?.gameID ?? data?.gameID ?? data?.tableId;
    if (gameID !== undefined && gameID !== null && gameID !== '') {
      // Stringified ONCE. This is the hottest path in the client (one call per
      // table frame, ~1/s/table), and the two consumers below both key by
      // String(gameID); calling it twice allocated a second string per frame.
      const key = String(gameID);
      this.feedWatchdog.noteFrame(key, monoNow());
      // A frame is proof this client's feed path works, which is the only thing
      // that refills the escalation budget - and it cancels a pending escalation
      // outright, so a feed that recovers inside the jitter wait costs nothing.
      this.feedEscalations = 0;
      this.feedEscalationDueAt = null;
      // Same rule, per table, for the heartbeat's budget: this table's feed is
      // provably delivering, so its beats have earned the right to be spent
      // again. A frame is the ONLY refill (see heartbeatsSinceFrame).
      this.heartbeatsSinceFrame.delete(key);
      return;
    }
    // A table frame whose gameID resolves to nothing would otherwise be dropped
    // in silence, and the watchdog would then judge that table silent and
    // re-subscribe it - a payload-shape change in the field would surface as
    // mystery repairs rather than as a log line. Rate-limited because this fires
    // per frame if it fires at all (raised in PR review).
    this.unattributedFrames += 1;
    const now = monoNow();
    if (now - this.lastUnattributedWarnAt > ConnectionManager.UNATTRIBUTED_WARN_EVERY_MS) {
      this.lastUnattributedWarnAt = now;
      console.warn('[ConnectionManager] table frame with no resolvable gameID', {
        total: this.unattributedFrames,
        keys: Object.keys(data || {}).slice(0, 8)
      });
    }
  };

  /**
   * Start the feed watchdog's tick. Idempotent - called on every update-socket
   * connect - and driven from here rather than a React effect so it follows the
   * socket's lifecycle rather than a component's. The watchdog itself decides
   * WHEN a table is due; this only performs the repair it names.
   */
  private startFeedWatchdog() {
    this.stopFeedWatchdog();
    if (typeof setInterval === 'undefined') return;
    this.feedWatchdogTimer = setInterval(() => {
      const socket: any = this.mode === 'dual' ? this.updateSocket : this.singleSocket;
      // Polled here rather than from the action socket's own connect/disconnect
      // handlers. Registering those is what the `off()` narrowing revert took
      // away (see setupIOListeners), and restoring them revives two real hazards:
      // `connect_error` reaching handleAuthError can clearAuth() + hardReload() a
      // seated member mid-hand on anything resembling a 401, and `disconnect`
      // calls socket.connect() directly, bypassing the configured backoff. A poll
      // reads the same `connected` flag with neither hazard, and it is what makes
      // PokerActions' "the action socket came back" re-send trigger work at all in
      // dual mode - the mode production runs. Verified dead there in QA: an action
      // socket that took no moves for 25s never moved this status off 'down'.
      this.recomputeActionPathStatus();
      // A dead connection is the keepalive's problem (missed pongs force a
      // reconnect). The watchdog only ever acts on a healthy connection.
      if (!socket?.connected) return;
      // Unconditional, and first: the heartbeat IS the repair on this path, so it
      // gets its chance to rewrite a lost row before the watchdog concludes
      // anything. In practice the watchdog's own repair now only fires when
      // heartbeats are themselves being refused - at which point it is a detector
      // for the escalation below rather than a remedy.
      this.runSubscriptionHeartbeats(monoNow());
      // canRepair is consulted BEFORE the watchdog spends a table's episode or
      // breaker credit, so a table we cannot actually re-subscribe (no tracked
      // subscription, or no usable playerGUID) is retried on a later tick
      // instead of being marked repaired and left silent forever.
      const due = this.feedWatchdog.tick(monoNow(), (gameID) => {
        const candidate = this.subscriptions.get(gameID);
        return !!candidate?.playerGUID;
      });
      due.forEach(({ gameID, silentForMs }) => {
        const sub = this.subscriptions.get(gameID);
        if (!sub) return;
        console.warn('[ConnectionManager] table feed silent - re-subscribing this table only', {
          gameID,
          silentForMs,
          repairs: this.feedWatchdog.repairCount,
          breakerOpen: this.feedWatchdog.breakerOpen
        });
        // Same reason as resyncTable: this repair can deliver a snapshot, and an
        // unflagged snapshot animates as live play. This table only.
        markTableResyncing(sub.gameID);
        this.assertSubscriptionOnUpdateSocket(sub);
      });
      this.escalateExhaustedTables();
    }, ConnectionManager.FEED_WATCHDOG_TICK_MS);
  }

  /**
   * A table whose feed the watchdog has repaired and which is STILL frameless.
   * The last remedy left is a reconnect, so that a fresh connectionId makes the
   * subscription genuinely new on the server.
   *
   * Why the watchdog does not do this itself: its whole safety argument is that
   * it never reconnects, because a reconnect blanks every table to fix one. That
   * argument holds for its ordinary repair, and it is why the repair is a
   * targeted per-table subscribe. But a per-table subscribe cannot fix a
   * per-CONNECTION failure, and that is the failure QA hit - measured in develop
   * on this build, 11 of 104 connections never had a single subscribe accepted,
   * and the client's only recovery was the player force-quitting the app. So the
   * escalation exists, and it lives here where it can be bounded across the
   * whole client rather than per table:
   *
   *   - it needs a table that had ALREADY proven a feed (the watchdog refuses to
   *     report any other), so a slow first join can never reach it;
   *   - it needs a repair to have gone out and produced nothing, so it is never
   *     the first thing tried;
   *   - it is reported once per silence episode per table, and every table due at
   *     once collapses into ONE reconnect;
   *   - a client-wide cooldown means a connection that keeps coming back broken
   *     cannot become a reconnect loop. The cooldown is deliberately much longer
   *     than the reconnect's own 500ms teardown delay plus a resubscribe round
   *     trip, so a reconnect always gets a fair chance to prove itself before
   *     another is allowed.
   *
   * Same remedy, and the same shape of bound, as
   * `hooks/useFeltHydrationRecovery` uses for a felt that never hydrated. This is
   * the sibling case it cannot see: a felt that hydrated, went stale, and can
   * never recover on the connection it is on.
   */
  private escalateExhaustedTables() {
    const now = monoNow();
    const exhausted = this.feedWatchdog.exhaustedTables(now);
    if (exhausted.length > 0 && this.feedEscalationDueAt === null) {
      this.feedEscalationDueAt =
        now + Math.floor(this.random() * ConnectionManager.FEED_ESCALATION_JITTER_MS);
    }
    if (this.feedEscalationDueAt === null) return;
    // A frame arriving inside the jitter wait clears the whole thing (see
    // noteTableFrame), so a client whose feed comes back on its own never
    // reconnects at all.
    if (now < this.feedEscalationDueAt) return;
    this.feedEscalationDueAt = null;

    if (this.feedEscalations >= ConnectionManager.MAX_FEED_ESCALATIONS) {
      // Once, not per tick: this is the steady state for the whole outage.
      if (this.feedEscalations === ConnectionManager.MAX_FEED_ESCALATIONS) {
        this.feedEscalations += 1;
        console.warn(
          '[ConnectionManager] table feed still dead after a fresh connection - standing down, this is not the connection',
          { tables: exhausted, escalations: ConnectionManager.MAX_FEED_ESCALATIONS }
        );
      }
      return;
    }
    if (now - this.lastFeedEscalationAt < ConnectionManager.FEED_ESCALATION_COOLDOWN_MS) {
      console.warn(
        '[ConnectionManager] table feed still dead after repair, but a reconnect is on cooldown',
        { tables: exhausted, sinceLastMs: Math.round(now - this.lastFeedEscalationAt) }
      );
      return;
    }
    this.lastFeedEscalationAt = now;
    this.feedEscalations += 1;
    console.warn(
      '[ConnectionManager] table feed still dead after repair - forcing ONE reconnect for a new connection id',
      {
        tables: exhausted,
        repairs: this.feedWatchdog.repairCount,
        escalation: this.feedEscalations
      }
    );
    this.reconnect('feed-escalation');
  }

  private stopFeedWatchdog() {
    if (this.feedWatchdogTimer) {
      clearInterval(this.feedWatchdogTimer);
      this.feedWatchdogTimer = null;
    }
  }

  /** Diagnostic: how many per-table feed repairs this manager has issued. */
  get feedRepairCount() {
    return this.feedWatchdog.repairCount;
  }

  /**
   * Derive the single connectivity status from the live socket(s) and publish it.
   * Called from every socket connect/disconnect so the status can never get
   * stranded by the order sockets happen to (re)connect in.
   *
   * In DUAL mode the status tracks the UPDATE socket (the API Gateway path) — in
   * the serverless-first architecture that's the socket that actually delivers
   * game/tournament state, so it's what determines whether the player is
   * "connected" for the purposes of the ConnectionWarning banner. Its silent-
   * death is already covered by the keepalive liveness watchdog (which forces a
   * disconnect → this recompute). The legacy action socket is a transitional
   * dual-write path; if it lags or isn't running (e.g. serverless-only envs) it
   * must NOT pin the "reconnecting" banner while the game is live on the update
   * socket — that was the stuck-banner bug. Single-mode tracks its one socket.
   */
  private recomputeStatus() {
    const primary = this.mode === 'dual' ? this.updateSocket : this.singleSocket;
    if (primary?.connected) {
      this.setStatus('connected');
    } else if (primary) {
      // Socket object exists but isn't open → it's retrying.
      this.setStatus('reconnecting');
    } else {
      this.setStatus('disconnected');
    }
  }

  private connectActionSocket(token: ReturnType<typeof getAuthToken>) {
    if (this.actionSocket) {
      this.actionSocket.disconnect();
    }
    this.actionSocket = this.createIOSocket(SOCKET_SERVER_URL, token);
  }

  private connectUpdateSocket(token: ReturnType<typeof getAuthToken>) {
    if (this.updateSocket) {
      this.updateSocket.disconnect();
    }

    if (UPDATE_SOCKET_URL) {
      this.updateSocket = this.createRawSocket(UPDATE_SOCKET_URL, token);
    }
  }

  private createIOSocket(url: string, token: ReturnType<typeof getAuthToken>): IOSocket {
    const s = io(url, {
      auth: buildSocketAuth(token),
      secure: false,
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000
    });
    this.setupIOListeners(s);
    // On the Manager, not the socket: consumers clear socket-level 'connect'
    // listeners with a handler-less off(CONNECT) (Lobby's effect cleanup), and
    // 'open' also covers the manual socket.connect() in the disconnect handler,
    // which Socket.IO does not report as a 'reconnect'.
    s.io.on('open', () => this.reannounceOnline(s));
    return s;
  }

  private ioSocketForEngineEmits(): IOSocket | null {
    if (this.mode === 'dual') return this.actionSocket;
    if (this.mode === 'single-io') return this.singleSocket as IOSocket | null;
    return null;
  }

  private rememberOnlinePlayer(playerGUID: unknown) {
    const socket = this.ioSocketForEngineEmits();
    if (!socket || typeof playerGUID !== 'string' || !playerGUID) return;
    this.onlinePlayerBySocket.set(socket, playerGUID);
  }

  /**
   * Re-register a re-opened action socket as the player it was announced as.
   * Without this the player keeps the lobby (Lobby re-joins its room on
   * connect) but silently stops receiving every per-player engine push until a
   * page refresh - EN-10562's REGISTERED banner that never clears. The emit is
   * buffered by Socket.IO until the namespace handshake completes.
   */
  private reannounceOnline(socket: IOSocket) {
    if (socket !== this.ioSocketForEngineEmits()) return;
    const playerGUID = this.onlinePlayerBySocket.get(socket);
    if (!playerGUID) return;
    socket.emit(ENGINE_ONLINE_EVENT, { playerGUID });
  }

  private createRawSocket(
    url: string,
    token: ReturnType<typeof getAuthToken>
  ): RawWebSocketAdapter {
    const s = new RawWebSocketAdapter(url, {
      access_token: token.access_token,
      refresh_token: token.refresh_token
    });
    this.setupSocketListeners(s);
    return s;
  }

  private setupIOListeners(socket: IOSocket) {
    // Stamp every inbound event for the HUD's action-socket "stream age" metric.
    // onAny fires for all events except the lifecycle ones below, which is
    // exactly the message traffic we want to measure liveness against. In
    // single-io mode, also note felt (gotTexas/gotOmaha) arrivals so the
    // foreground staleness gate sees the table's frames. (The feed watchdog
    // does not run in single-io: only the update socket's connect starts it.)
    socket.onAny((event: string, ...args: any[]) => {
      recordActionMessage();
      // noteTableFrame, not noteFeltUpdate: it does the felt gate AND attributes
      // the frame to a table for the feed watchdog.
      //
      // Not in dual mode: there the UI consumes table state from the update
      // socket only (see DUAL_LISTEN_EVENTS), so an engine frame proves nothing
      // about what is rendered. Counting it would reset the watchdog while the
      // update feed is dead and block the recovery. The engine does not push
      // table state today (`run()`'s `multiProcess` loop is commented out in
      // apps/engine/texas/socket.js), so this guards against that changing.
      if (this.mode !== 'dual' && (event === 'gotTexas' || event === 'gotOmaha')) {
        this.noteTableFrame(args[0]);
      }
    });

    socket.on('connect', () => {
      console.log('[ConnectionManager] Socket.IO connected, id:', socket.id);
      this.recomputeStatus();
      this.recomputeActionPathStatus();
      this.resubscribeAll();
      // resubscribeAll has just asserted every row for the new connectionId, so
      // no table is due for a full interval - this keeps the heartbeat from
      // piling straight onto the reconnect it follows.
      this.deferHeartbeats(monoNow());
    });

    socket.on('disconnect', (reason: string) => {
      console.log('[ConnectionManager] Socket.IO disconnected:', reason);
      // Recompute the aggregate status. In dual mode the banner tracks the
      // UPDATE socket (see recomputeStatus), so a legacy action-socket drop
      // alone won't pin "reconnecting" while the serverless path is live —
      // that was the stuck-banner bug. Socket.IO auto-retries (reconnection:true)
      // and its 'connect' handler recomputes again once it's back.
      this.recomputeStatus();
      // Unlike ConnectionStatus, the action path DOES go down when this socket
      // does — that is the whole point of publishing it separately.
      this.recomputeActionPathStatus();
      if (
        reason === 'io server disconnect' ||
        reason === 'Server disconnect' ||
        reason === 'transport close'
      ) {
        // Update auth and reconnect
        const freshToken = getAuthToken();
        if (freshToken.access_token) {
          socket.auth = {
            access_token: freshToken.access_token,
            refresh_token: freshToken.refresh_token
          };
          socket.connect();
        }
      }
    });

    socket.on('connect_error', (error: any) => {
      console.log('[ConnectionManager] Socket.IO connect_error:', error?.message);
      this.handleAuthError(error);
    });

    // Handle token refresh from engine
    socket.on('gotRefreshToken', (data: any) => {
      this.handleTokenRefresh(data);
    });
  }

  private setupSocketListeners(socket: SocketIOLikeInterface) {
    // Note felt (gotTexas/gotOmaha) arrivals so the foreground staleness gate can
    // tell a re-subscribe re-hydrated the table, and so the per-table feed
    // watchdog knows which tables are alive (the API Gateway broadcaster pushes
    // these over the update socket - it is the ONLY path table frames take).
    //
    // Through the adapter's owner tap, NOT `socket.on(...)`. `App.tsx`'s effect
    // cleanup calls `socket.off(GOT_TEXAS)` with no handler, and `off` without a
    // handler deletes the entire handler Set for that event - so a listener
    // registered here was removed the first time that effect re-ran, and this
    // manager went deaf to table frames for the rest of the session. See the
    // `onAnyEvent` field note in RawWebSocketAdapter for the measurement.
    // A stable function reference, so re-running this for the same adapter adds
    // no duplicate tap (Set dedupes by identity).
    socket.addTap?.(this.tapTableFrames);

    socket.on('connect', () => {
      console.log('[ConnectionManager] API Gateway WebSocket connected');
      // In single-socket mode THIS socket carries the actions, so it owns the
      // action-path status. In dual mode the action socket owns it and this is a
      // no-op (recomputeActionPathStatus reads whichever socket the mode says).
      if (this.mode !== 'dual') this.recomputeActionPathStatus();
      // Update socket is the primary connectivity signal in dual mode → its
      // reconnect clears the banner on its own, regardless of the legacy action
      // socket's state.
      this.recomputeStatus();
      // A new connection id means every table's fan-out row is being rewritten
      // by resubscribeAll below. Start the watchdog's clocks from here so it
      // does not fire on silence that predates this connection.
      // Passing `now` lets tables that had ALREADY proven a feed be re-judged
      // against the shorter post-reconnect grace. Without it they are invisible
      // to the watchdog until they produce a frame - which is precisely what a
      // rejected re-subscribe prevents. See resetClock().
      this.feedWatchdog.resetClock(monoNow());
      this.startFeedWatchdog();
      this.resubscribeAll();
      // resubscribeAll has just asserted every row for the new connectionId, so
      // no table is due for a full interval. This is the leg the fan-out rows
      // belong to, so it is the one that matters: without it the heartbeat can
      // beat immediately after the reconnect and double-assert every table.
      this.deferHeartbeats(monoNow());
    });

    socket.on('disconnect', (reason: any) => {
      console.log('[ConnectionManager] API Gateway WebSocket disconnected:', reason);
      if (this.mode !== 'dual') this.recomputeActionPathStatus();
      this.stopFeedWatchdog();
      this.recomputeStatus();
    });

    socket.on('connect_error', (error: any) => {
      console.log('[ConnectionManager] API Gateway WebSocket error:', error?.message || error);
      // Update token on the adapter so the next reconnect attempt uses fresh creds
      const freshToken = getAuthToken();
      if (freshToken.access_token && socket.auth) {
        socket.auth = {
          access_token: freshToken.access_token,
          refresh_token: freshToken.refresh_token
        };
      }
    });

    socket.on('reconnect_failed', () => {
      console.log('[ConnectionManager] API Gateway WebSocket reconnect exhausted — full reconnect');
      // RawWebSocketAdapter gave up after max attempts; do a full reconnect with fresh tokens
      const freshToken = getAuthToken();
      if (freshToken.access_token) {
        this.connectUpdateSocket(freshToken);
      }
    });
  }

  private disconnectSockets() {
    this.stopFeedWatchdog();
    this.setActionPathStatus('down');
    if (this.actionSocket) {
      this.actionSocket.removeAllListeners();
      this.actionSocket.disconnect();
      this.actionSocket = null;
    }
    if (this.updateSocket) {
      this.updateSocket.disconnect();
      this.updateSocket = null;
    }
    if (this.singleSocket) {
      (this.singleSocket as any).removeAllListeners?.();
      (this.singleSocket as any).disconnect();
      this.singleSocket = null;
    }
  }

  private resubscribeAll() {
    if (this.subscriptions.size === 0 && this.tournamentSubscriptions.size === 0) return;

    // actionSocket and updateSocket each independently call resubscribeAll()
    // from their own 'connect' handler, and both typically (re)connect close
    // together (they're kicked off by the same connect() call, or by the same
    // checkAndReconnect() cycle). SubscribeToGameUpdates is a DUAL_EMIT event
    // — one emit already reaches both sockets — so two independent
    // resubscribeAll() calls send the API Gateway lambda two concurrent
    // SubscribeToGameUpdates for the same table. Those race on the same
    // DynamoDB connection item; the loser gets a TransactionConflict and its
    // fan-out row never gets written, producing a stuck "no GOT_TEXAS" page.
    // Coalesce both connect events into a single actual resubscribe.
    if (this.resubscribeTimer) return;
    this.resubscribeTimer = setTimeout(() => {
      this.resubscribeTimer = null;
      if (this.subscriptions.size === 0 && this.tournamentSubscriptions.size === 0) return;
      console.log(
        '[ConnectionManager] Re-subscribing to',
        this.subscriptions.size,
        'table(s) and',
        this.tournamentSubscriptions.size,
        'tournament(s)'
      );

      // NOTE: do NOT emit registerSocket{page:'game', gameID} per subscribed
      // table here. The engine treats that as "this connection's current game"
      // (last write wins), so iterating over multiple subscribed tables would
      // clobber whichever table the URL is actually on and pull the user back
      // to a different table — particularly visible when mixing texas+omaha.
      // The single-table Game view's REGISTER_SOCKET useEffect re-fires for
      // the URL table when the socket reference changes (status transitions
      // produce a new proxy via ConnectionManagerContext.setSocket), so the
      // engine's current-game registration stays correct on reconnect.
      this.subscriptions.forEach((sub) => {
        // Emit DIRECTLY to the underlying sockets rather than through
        // `getProxy().emit`. The proxy adds a wire-level dedup that skips
        // `SubscribeToGameUpdates` when the gameID is already tracked in
        // `this.subscriptions`, which is exactly the case during replay —
        // routing through the proxy here would no-op every replay call and
        // leave the reconnected connectionId with zero server-side fan-out
        // rows. Mirrors the DUAL_EMIT_EVENTS branch of the proxy for this
        // event: hit both sockets in dual mode, singleSocket otherwise.
        //
        // Never replay an empty playerGUID. The engine removes this socket's
        // table entry and only then validates the GUID, aborting before it
        // re-adds the entry — so a replay carrying '' turns a correctly
        // registered player into an unregistered one, silently. A tracked entry
        // with no GUID is unusable rather than merely imperfect, so skip it and
        // let the view layer's own subscribe (which has the GUID) re-establish
        // it. See trackSubscription.
        if (!sub.playerGUID) {
          console.warn(
            '[ConnectionManager] Skipping resubscribe with empty playerGUID for table',
            sub.gameID
          );
          return;
        }
        const payload = buildSubscribePayload(sub);
        if (this.mode === 'dual') {
          this.actionSocket?.emit('SubscribeToGameUpdates', payload);
          this.updateSocket?.emit('SubscribeToGameUpdates', payload);
        } else {
          (this.singleSocket as any)?.emit('SubscribeToGameUpdates', payload);
        }
      });

      // Re-subscribe the tournament (gotTnmStatus) feeds too. Mirrors
      // GameService.subscribeTournamentDetails: the API-Gateway subscribeTournament
      // re-establishes the serverless broadcast, and registerSocket{page:'tournament'}
      // re-registers with the legacy engine. Without this a reconnect leaves the
      // player on stale tournament state (frozen rebalancing overlay, missed
      // bust/redirect) in every view — single-table hits it hardest because its
      // tournament set is stable, so the App-level subscription never re-fires.
      //
      // Defense-in-depth: the proxy emit guard above already rejects invalid
      // tournamentIds before they enter tournamentSubscriptions, but any bad ID
      // that somehow slipped in (older client build carrying legacy state, a
      // test injecting one directly, a future caller mutating the set) must
      // never be replayed to the wire. Filter here too so a reconnect can never
      // resurrect a `subscribeTournament(-1)`.
      if (this.tournamentSubscriptions.size > 0) {
        const playerGUID = localStorage.getItem('playerGUID') || '';
        this.tournamentSubscriptions.forEach((tournamentId) => {
          if (!isValidTournamentId(tournamentId)) {
            // eslint-disable-next-line no-console
            console.warn(
              '[ConnectionManager] Skipping resubscribe for invalid tournamentId:',
              tournamentId
            );
            return;
          }
          const proxy = this.getProxy();
          proxy.emit('subscribeTournament', { tournamentId, playerGUID });
          proxy.emit('registerSocket', {
            page: 'tournament',
            tournamentId,
            isRegister: true
          });
        });
      }
    }, 150);
  }

  private handleTokenRefresh(data: any) {
    if (data?.error === 'invalid_grant') {
      // Guard: an upstream Descope hiccup should not nuke a client whose
      // current access_token is still exp-valid. Mirrors the same-named guard
      // in App.tsx's GOT_REFRESH_TOKEN handler — both paths converge on the
      // same teardown so they need the same defense. Instead of silently
      // ignoring the invalid_grant (which leaves the client hung if the
      // token was really revoked server-side — exp still future, but
      // Descope refuses to talk to it), we kick a client-side refresh to
      // verify. Real revocation → terminal short-circuit → session-end;
      // transient engine hiccup → refresh succeeds and we recover.
      import('utils/auth').then(
        ({ canAttemptRefresh, clearAuth, hardReload, hasValidAuthToken, verifyRefreshNow }) => {
          // canAttemptRefresh() covers the case hasValidAuthToken() alone gets
          // backwards: an access_token past exp but backed by a live refresh
          // token is the one state a refresh is FOR, and testing exp-validity
          // alone tore it down instead of renewing it.
          if (hasValidAuthToken() || canAttemptRefresh()) {
            console.warn(
              '[ConnectionManager] Refresh token invalid but session still refreshable — verifying with Descope now'
            );
            verifyRefreshNow();
            return;
          }
          console.log('[ConnectionManager] Refresh token invalid — clearing session');
          clearAuth();
          hardReload();
        }
      );
      return;
    }

    const tokenStr = JSON.stringify(data);
    Cookies.set(DESCOPE_COOKIE as string, tokenStr, { path: '/' });
    Cookies.set(DESCOPE_COOKIE as string, tokenStr as any, {
      path: '/',
      domain: `.${APP_DOMAIN}`
    });
    try {
      localStorage.setItem(DESCOPE_COOKIE as string, tokenStr);
    } catch {
      /* noop */
    }

    // Update socket auth
    if (this.actionSocket) {
      this.actionSocket.auth = {
        access_token: data.access_token,
        refresh_token: data.refresh_token
      };
    }

    console.log('[ConnectionManager] Token refreshed');
  }

  private handleAuthError(error: any) {
    const msg = error?.message || error?.toString() || '';
    // Structured detection first — any transport-level error can carry a `.code`
    // or a `.data.code` from the server. Only fall back to substring matching
    // when neither is present. This closes a common false-positive: a proxy
    // 502 whose HTML body mentions "401" used to trip the raw msg.includes
    // check and hard-reload a live member.
    const structuredCode =
      Number(error?.code) ||
      Number(error?.data?.code) ||
      Number(error?.status) ||
      Number(error?.data?.status) ||
      null;
    // Substring is allowed as a last-resort signal when the structured code
    // isn't itself an HTTP status (Engine.IO / Socket.IO transport codes are
    // small integers 0-9, not 4xx). Only a KNOWN non-auth HTTP status (5xx,
    // 3xx, other 4xx) is trusted to suppress the substring fallback — a raw
    // "Unauthorized" in the message from a legacy engine path with a
    // transport-level `code: 4` would otherwise be silently ignored.
    const isKnownNonAuthHttpStatus =
      structuredCode !== null &&
      structuredCode >= 300 &&
      structuredCode < 600 &&
      structuredCode !== 401 &&
      structuredCode !== 403;
    const looksAuth =
      structuredCode === 401 ||
      structuredCode === 403 ||
      error?.data?.code === 'AUTH_EXPIRED' ||
      error?.data?.code === 'UNAUTHORIZED' ||
      (!isKnownNonAuthHttpStatus && (msg.includes('Unauthorized') || msg.includes('401')));

    if (looksAuth) {
      // Guests have no token to clear or refresh — clearAuth()+reload() would
      // just reconnect, get rejected again, and reload forever. Leave the
      // socket to retry quietly instead of reloading the page.
      if (isGuestSession()) {
        console.log('[ConnectionManager] Guest auth error — skipping reload');
        return;
      }
      import('utils/auth').then(
        ({ canAttemptRefresh, clearAuth, hardReload, hasValidAuthToken, verifyRefreshNow }) => {
          // The engine refused an expired handshake, but we already hold a
          // newer, valid token: our own refresh landed while that handshake
          // was in flight. Reconnect with it instead of spending the refresh
          // token again. Once per token, so an engine that keeps refusing the
          // same token falls through to the verification refresh below.
          if (error?.data?.code === 'AUTH_EXPIRED' && hasValidAuthToken()) {
            const { access_token, refresh_token } = getAuthToken();
            if (
              this.actionSocket &&
              access_token &&
              access_token !== this.expiredHandshakeRetryToken
            ) {
              this.expiredHandshakeRetryToken = access_token;
              this.actionSocket.auth = { access_token, refresh_token };
              this.actionSocket.connect();
              return;
            }
          }
          // Same guard rationale as handleTokenRefresh: only nuke the session
          // when it's genuinely beyond saving — no live refresh token, or a
          // family Descope has already repudiated. "exp still future" is a
          // weaker signal than the engine's 401, so kick an immediate
          // verification refresh instead of silently ignoring. Real
          // revocation → terminal short-circuit → session-end. Transient
          // upstream error → refresh succeeds and we continue. And an
          // access_token past exp with a live refresh token behind it is the
          // case a refresh is FOR — canAttemptRefresh() keeps it out of the
          // teardown branch it used to fall straight into.
          if (hasValidAuthToken() || canAttemptRefresh()) {
            console.warn(
              '[ConnectionManager] Auth error but session still refreshable — verifying with Descope now',
              { code: structuredCode, msg }
            );
            verifyRefreshNow();
            return;
          }
          console.log('[ConnectionManager] Auth error — clearing session', {
            code: structuredCode
          });
          clearAuth();
          hardReload();
        }
      );
    } else {
      // Try reconnecting with fresh token
      const freshToken = getAuthToken();
      if (this.actionSocket && freshToken.access_token) {
        this.actionSocket.auth = {
          access_token: freshToken.access_token,
          refresh_token: freshToken.refresh_token
        };
        this.actionSocket.connect();
      }
    }
  }

  // ── Lifecycle handlers ──

  private handleVisibility = () => {
    if (document.visibilityState === 'visible') {
      console.log('[ConnectionManager] App visible — checking connections');
      this.checkAndReconnect();
    } else if (document.visibilityState === 'hidden') {
      this.markAway();
    }
  };

  private handleFocus = () => {
    console.log('[ConnectionManager] Window focus — checking connections');
    this.checkAndReconnect();
  };

  private handleBlur = () => {
    this.markAway();
  };

  /** Stamp the moment the app went away, so the foreground handler can measure
   *  how long we were gone and skip the re-hydrate for trivial focus toggles.
   *  Only overwrite on the FIRST "away" signal in a session — later signals
   *  (e.g. visibility=hidden AND then blur) would otherwise reset the clock. */
  private markAway() {
    if (this.lastAwayAt === null) {
      this.lastAwayAt = Date.now();
    }
  }

  private async setupCapacitorListener() {
    try {
      const { App } = await import('@capacitor/app');
      const handle = await App.addListener('appStateChange', (state: { isActive: boolean }) => {
        console.log('[ConnectionManager] appStateChange:', state.isActive);
        if (state.isActive) {
          this.checkAndReconnect();
        } else {
          this.markAway();
        }
      });
      this.appListenerRemove = () => handle.remove();
    } catch {
      // Not on Capacitor
    }
  }

  private checkAndReconnect() {
    if (this.destroyed) return;

    // The initial connect() call is still in flight — its own handshake will
    // resolve to 'connected' or hit connect_error (handleAuthError) on its
    // own. This matters because 'focus' fires almost immediately after a
    // fresh page load reaches the foreground (including our post-login
    // hard-reload landing straight on a table), often before that first
    // handshake completes. Forcing a reconnect here races a socket that
    // hasn't finished connecting yet ("WebSocket is closed before the
    // connection is established"), which then makes both sockets reconnect
    // close together and each independently call resubscribeAll() — see its
    // dedupe guard for why that produces a stuck "no GOT_TEXAS" page.
    if (this._status === 'connecting') return;

    // Capture and CLEAR the away timestamp up-front, before any branch below
    // early-returns. The reconnect path and the "still connecting" guard both
    // used to leave `lastAwayAt` pinned at its original stamp; with the
    // 30 s foreground-resubscribe gate, that meant a returning-with-dead-
    // socket path stamped an ever-growing awayMs for the rest of the session
    // and the gate was effectively disabled. Reset here so every entry to
    // checkAndReconnect starts fresh regardless of which branch executes.
    const awayAt = this.lastAwayAt;
    this.lastAwayAt = null;

    const ioConnected =
      this.mode === 'dual' ? this.actionSocket?.connected : (this.singleSocket as any)?.connected;

    const wsConnected =
      this.mode === 'dual'
        ? // A half-open WS still reports connected===true; isStale() catches the
          // case where keepalive pongs have already gone missing, so returning to
          // the app forces a reconnect immediately instead of waiting for the
          // adapter's own keepalive watchdog to reach the dead threshold.
          this.updateSocket?.connected && !this.updateSocket?.isStale()
        : true; // no separate WS in single mode

    console.log('[ConnectionManager] Connection check — IO:', ioConnected, 'WS:', wsConnected);

    if (!ioConnected || !wsConnected) {
      // Get fresh token and reconnect
      const token = getAuthToken();
      if (!token.access_token) {
        console.log('[ConnectionManager] No token — cannot reconnect');
        return;
      }

      if (!ioConnected) {
        console.log('[ConnectionManager] Socket.IO not connected — forcing reconnect');
        if (this.mode === 'dual' && this.actionSocket) {
          // Force disconnect first to clear stale transport state
          this.actionSocket.disconnect();
          this.actionSocket.auth = {
            access_token: token.access_token,
            refresh_token: token.refresh_token
          };
          this.actionSocket.connect();
        } else if (this.singleSocket && 'auth' in this.singleSocket) {
          (this.singleSocket as any).disconnect();
          (this.singleSocket as any).auth = {
            access_token: token.access_token,
            refresh_token: token.refresh_token
          };
          (this.singleSocket as any).connect();
        }
      }

      if (!wsConnected && this.updateSocket) {
        console.log(
          '[ConnectionManager] API Gateway WS not connected — updating token and reconnecting'
        );
        // Update auth on existing adapter (preserves event handlers) and reconnect
        this.updateSocket.auth = {
          access_token: token.access_token,
          refresh_token: token.refresh_token
        };
        this.updateSocket.connect();
      } else if (!wsConnected && !this.updateSocket && this.mode === 'dual') {
        // Adapter was destroyed — create fresh
        console.log('[ConnectionManager] API Gateway WS adapter missing — creating new');
        this.connectUpdateSocket(token);
      }
    } else {
      // Both sockets are still connected on return-from-idle. The connection is
      // live but the table can be un-hydrated / its feed went silent while we were
      // away — an idle table emits no organic TABLE_UPDATE, so nothing arrives and
      // the felt stays blank (RCA 20260713 item C). First try the CHEAP path:
      // re-subscribe (idempotent + coalesced; 5s debounce stops rapid tab-toggles
      // fanning a storm). NOTE: a same-connection re-subscribe does NOT itself
      // re-hydrate an idle felt — the server pushes a table snapshot only for a
      // NEW subscription (subscribeGame gates on isNewSubscription; snapshot-on-
      // subscribe is still on hold, see the design-plan doc). So we ALSO arm a
      // staleness gate: if no gotTexas/gotOmaha lands shortly after, force a full
      // reconnect (fresh connectionId ⇒ NEW subscription ⇒ snapshot). The reconnect
      // only fires when the cheap path demonstrably failed, so healthy returns stay
      // reconnect-free. See docs/investigations/20260713-snapshot-on-subscribe-design-plan.md.
      const nowMs = Date.now();

      // Coming back from the background is the most common way to find a table
      // whose feed died meanwhile, and the per-table feed watchdog is what
      // covers it now: its tick resumes with the socket, and a table that stays
      // silent past the threshold gets exactly one targeted re-subscribe. (An
      // earlier EN-10628 draft re-asserted every table on the ACTION socket
      // here; withdrawn once it was established that the engine's registration
      // is not in the path frames take to this client.)

      // Only run the "re-hydrate just in case" path when we were AWAY long
      // enough for it to plausibly matter. A trivial focus toggle (DevTools
      // click, dropdown flicker, modal focus swap) doesn't blank a felt or
      // silence a feed; running resubscribeAll for it is pure noise on the
      // wire (SubscribeToGameUpdates → Lambda) and in the log. The reconnect
      // path above is unaffected — a genuinely dead socket still reconnects
      // regardless of how long we were away. `awayAt` was captured and cleared
      // at the top of this method so every branch — reconnect, connecting-
      // early-return, and this healthy branch — resets the timestamp uniformly.
      const awayMs = awayAt === null ? 0 : nowMs - awayAt;
      // Ahead of the 30s gate: a shorter background still needs every table
      // re-asserted, it just does not need a full resubscribeAll to do it.
      if (awayMs >= ConnectionManager.MIN_AWAY_FOR_HEARTBEAT_PULL_MS) {
        this.pullHeartbeatsForward(monoNow(), `foreground after ${Math.round(awayMs / 1000)}s`);
      }
      if (awayMs < ConnectionManager.MIN_AWAY_FOR_FOREGROUND_RESUBSCRIBE_MS) {
        return;
      }
      if (this.subscriptions.size > 0 && nowMs - this.lastForegroundResync > 5000) {
        this.lastForegroundResync = nowMs;
        console.log(
          '[ConnectionManager] Foreground with live sockets — re-subscribing to re-hydrate',
          '(away for',
          Math.round(awayMs / 1000),
          's)'
        );
        // The engine re-pushes current table state on re-subscribe — a background
        // resync of tables the player is already viewing. Flag it so the seat
        // rotation snaps rather than replaying the entrance rotation. Client-wide:
        // resubscribeAll re-asserts every table.
        markAllTablesResyncing();
        this.resubscribeAll();
        // resubscribeAll has just asserted every table, so the heartbeat starts a
        // fresh interval rather than beating again on the next tick.
        this.deferHeartbeats(monoNow());
        this.armFeltStalenessGate();
      }
    }
  }
}
